import { useLabelVersion } from '../../../../features/labels/LabelsProvider';
import { labelId } from '../../../../features/labels/labels';
import React, { useEffect, useMemo, useRef, useState, useLayoutEffect, useContext, createContext } from "react";
import ReportGenerator from "./components/ReportGenerator";
import { exportNanooxReportDark  } from './components/exportNanooxReport';
import { GeneratorsStatus } from "./components/Generators";
import { getStatusMessage, useCenterStatus } from "./utils/statusUtils";
import DrawerNanoox from "../Home/layout/components/DrawerNanoox";
import Logo from '../../assets/img/ITG-Logotipo-SobreNegro.png'
import LogoNanoox from '../../assets/img/APP_Nanoox_sin fondo.png'
import CustomTooltip from "./components/CustomTooltip";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ErrorOutline, WarningAmber } from "@mui/icons-material";
import EventNoteIcon from '@mui/icons-material/EventNote';
import LoupeIcon from '@mui/icons-material/Loupe';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ViewStreamRoundedIcon from '@mui/icons-material/ViewStreamRounded';
import ViewColumnRoundedIcon from '@mui/icons-material/ViewColumnRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import { Modal, Box, Skeleton } from "@mui/material";
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  TimeScale,
} from "chart.js";
import "chartjs-adapter-date-fns"; // para manejar timestamps en el eje X
import annotationPlugin from "chartjs-plugin-annotation";
import {
  formatHistoryEntry,
  getHistoryTone,
  translateText as t,
} from "../../shared/utils/historyTranslation";
import "./dashboard.css";
import SummaryDashboardV3View from "./SummaryDashboardV3/SummaryDashboardV3";
import { groupCentersWithoutData } from "./dashboardCenterStatus";

ChartJS.register(
  annotationPlugin,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  TimeScale
);

const DASHBOARD_CENTER_ORDER_KEY = "nanoox_dashboard_center_order";
const DASHBOARD_LAYOUT_CHANGE_EVENT = "nanoox-dashboard-layout-change";
const DASHBOARD_VIEW_MODE_KEY = "nanoox_dashboard_view_mode";
const DASHBOARD_VIEW_MODES = new Set(["current", "legacy", "summary-v3"]);

const DASHBOARD_PS_ALARM_KEYS = {
  A01: "line_voltage_fail",
  A02: "phase_loss",
  A03: "phase_sequence_error",
  A04: "freq_out_of_range",
  A05: "aux_voltage_fail",
  A06: "asymmetric_current",
  A07: "overcurrent_protection",
  A08: "blocked_rotor",
  A09: "low_load",
  A10: "long_startup",
  A11: "bypass_relay_fail",
  A12: "thermal_pre_alarm_motor",
  A13: "thermal_pre_alarm_starter",
  A14: "thermal_prot_motor",
  A15: "thermal_prot_starter",
  A16: "short_circuit_l1t1",
  A17: "short_circuit_l3t3",
  A18: "temp_sensor_fail",
  A19: "low_line_voltage",
  A20: "high_line_voltage",
  A21: "low_motor_current",
  A22: "maintenance_request",
  A23: "fan_fail",
  A24: "blocked_fan",
  A25: "system_error",
  UA01: "ua01_alarm",
  UA02: "ua02_alarm",
  UA03: "ua03_alarm",
  UA04: "ua04_alarm",
};

function translateDashboardStatusMessage(message, translate) {
  if (!message) return message;
  const source = String(message);
  const normalized = source
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  if (normalized.includes("sin comunicaci")) return t(translate, "device_com", "SIN COMUNICACION");
  if (normalized.includes("parada de emergencia")) return t(translate, "parada_emergencia", "PARADA DE EMERGENCIA");
  if (normalized === "alerta") return t(translate, "alerta", "Alerta");

  const alarmMatch = source.match(/^([A-Z]{1,2}\d{2})\s*-\s*(.+)$/);
  if (alarmMatch) {
    const [, code, description] = alarmMatch;
    const key = DASHBOARD_PS_ALARM_KEYS[code];
    return `${code} - ${key ? t(translate, key, description) : description}`;
  }

  return source;
}

function emitDashboardLayoutChange() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(DASHBOARD_LAYOUT_CHANGE_EVENT));
}

function readStoredDashboardViewMode() {
  if (typeof window === "undefined") return "current";
  const storedMode = window.localStorage.getItem(DASHBOARD_VIEW_MODE_KEY);
  return DASHBOARD_VIEW_MODES.has(storedMode) ? storedMode : "current";
}

function getStoredCentroKey() {
  if (typeof window === "undefined") {
    return null;
  }

  const centro = window.localStorage.getItem("CENTRO") || "";
  const area = window.localStorage.getItem("AREA") || "";
  const region = window.localStorage.getItem("REGION") || "";

  if (!centro || !area || !region) {
    return null;
  }

  return `${centro}-${area}-${region}`;
}


function formatElapsedSinceMeasurement(value) {
  const measuredAt = parseDashboardDateTime(value);
  if (!measuredAt) return "";

  const diffMs = Date.now() - measuredAt.getTime();
  if (diffMs <= 0) return "justo ahora";

  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (days > 0) parts.push(`${days} día${days !== 1 ? "s" : ""}`);
  if (hours > 0) parts.push(`${hours} hora${hours !== 1 ? "s" : ""}`);
  if (minutes > 0 || parts.length === 0) {
    parts.push(`${minutes} minuto${minutes !== 1 ? "s" : ""}`);
  }

  return `hace ${parts.join(" ")}`;
}


function normalizeDashboardKey(value) {
  return String(value ?? "").trim().toLowerCase();
}

function getCenterFullId(center) {
  return center?.centro || center?.modulos?.[0]?.cans?.[0]?.centro_id || "";
}

function getCenterDisplayName(center) {
  const fullId = getCenterFullId(center);
  if (!fullId) return "—";

  const parts = String(fullId).split("-");
  return parts[parts.length - 1]?.trim() || "—";
}

function getCenterOrderKey(center) {
  const fullId = getCenterFullId(center);
  if (fullId) return normalizeDashboardKey(fullId);

  return normalizeDashboardKey(
    `${center?.region ?? ""}-${center?.area ?? ""}-${center?.centro ?? ""}`
  );
}

function isRenderableCenter(center) {
  const name = getCenterDisplayName(center);
  return Boolean(name) && name !== "—" && name !== "Ã¢â‚¬â€" && name !== "â€”";
}

function hasActiveCage(bomba) {
  if (!bomba) return false;

  const lm = bomba?.latest_measurements || {};
  const hasMeasurements =
    toArray(lm?.O2).length > 0 ||
    toArray(lm?.SO2).length > 0 ||
    toArray(lm?.Temp).length > 0 ||
    toArray(lm?.TS).length > 0;

  return Boolean(
    bomba?.bomba_id != null ||
    bomba?.alias ||
    hasMeasurements ||
    isNum(Number(bomba?.O2)) ||
    isNum(Number(bomba?.O2_bckp)) ||
    isNum(Number(bomba?.SO2)) ||
    isNum(Number(bomba?.Temp))
  );
}

function getPumpSelectionKey(bomba, context = {}) {
  const parts = [
    context.centerId ?? bomba?.centro_id,
    context.moduleId ?? bomba?.modulo,
    context.canId ?? bomba?.can_id ?? bomba?.can,
    bomba?.bomba_id ?? bomba?.alias ?? context.index,
  ];

  const key = parts
    .map((part) => normalizeDashboardKey(part))
    .filter(Boolean)
    .join("|");

  return key || `pump-${context.index ?? "unknown"}`;
}

function getSelectedPumpKey(item) {
  return item?.__selectionKey || item?.selectionKey || getPumpSelectionKey(item);
}

function sanitizeDashboardCenter(center) {
  if (!center || !isRenderableCenter(center)) return null;

  const modulos = toArray(center?.modulos)
    .map((modulo) => {
      const cans = toArray(modulo?.cans)
        .map((can) => {
          const bombas = toArray(can?.bombas).filter(hasActiveCage);
          return bombas.length ? { ...can, bombas } : null;
        })
        .filter(Boolean);

      return cans.length ? { ...modulo, cans } : null;
    })
    .filter(Boolean);

  return modulos.length ? { ...center, modulos } : null;
}

function getActiveCageCount(center) {
  return toArray(center?.modulos).reduce(
    (moduleCount, modulo) =>
      moduleCount +
      toArray(modulo?.cans).reduce(
        (canCount, can) => canCount + toArray(can?.bombas).length,
        0
      ),
    0
  );
}

function getCenterStatusLabel(status, translate) {
  const labels = {
    comap_only: t(translate, "dashboard_status_comap_only", "Solo datos COMAP"),
    sin_estructura: t(translate, "dashboard_status_without_structure", "Sin estructura lógica"),
    sin_datos_recientes: t(translate, "dashboard_status_without_recent_data", "Sin datos recientes"),
    error: t(translate, "dashboard_status_error", "Error de consulta"),
  };

  return labels[status] || t(translate, "dashboard_status_without_cages", "Sin datos de jaulas");
}

function CentersWithoutDataGroups({ groups, translate }) {
  return (
    <div className="dashboard-centers-groups">
      {groups.map((group) => (
        <div className="dashboard-centers-groups__line" key={group.status}>
          <strong>{getCenterStatusLabel(group.status, translate)} ({group.count}):</strong>
          <span>{group.namesLabel}</span>
          {group.details.length ? <small>{group.details.join(" / ")}</small> : null}
        </div>
      ))}
    </div>
  );
}

function buildOrderedCenterKeys(centers, previousKeys = []) {
  const availableKeys = centers
    .map((center) => getCenterOrderKey(center))
    .filter(Boolean);
  const uniqueAvailableKeys = [...new Set(availableKeys)];
  const persistedKeys = previousKeys.filter((key) =>
    uniqueAvailableKeys.includes(key)
  );

  return [
    ...persistedKeys,
    ...uniqueAvailableKeys.filter((key) => !persistedKeys.includes(key)),
  ];
}

function orderCentersByKeys(centers, orderedKeys = []) {
  const centerMap = new Map(
    centers.map((center) => [getCenterOrderKey(center), center])
  );

  return buildOrderedCenterKeys(centers, orderedKeys)
    .map((key) => centerMap.get(key))
    .filter(Boolean);
}

function moveCenterKey(keys, activeKey, overKey) {
  if (!activeKey || !overKey || activeKey === overKey) return keys;

  const nextKeys = [...keys];
  const fromIndex = nextKeys.indexOf(activeKey);
  const toIndex = nextKeys.indexOf(overKey);

  if (fromIndex < 0 || toIndex < 0) return keys;

  const [movedKey] = nextKeys.splice(fromIndex, 1);
  nextKeys.splice(toIndex, 0, movedKey);

  return nextKeys;
}

function readStoredCenterOrder() {
  if (typeof window === "undefined") return [];

  try {
    const rawValue = window.localStorage.getItem(DASHBOARD_CENTER_ORDER_KEY);
    const parsedValue = JSON.parse(rawValue || "[]");
    return Array.isArray(parsedValue) ? parsedValue.filter(Boolean) : [];
  } catch {
    return [];
  }
}

/* =========================
   Contexto de Hover Global
   ========================= */


const ScrollCtx = createContext({
  globalPaused: false,
  globalSpeed: 15, // segundos por ciclo
  animationPaused: false,
  toggleGlobalPaused: () => {},
  setGlobalPaused: () => {},
  setGlobalSpeed: () => {},
});

export function ScrollProvider({ children, externalPaused = false }) {
  const [globalPaused, setGlobalPaused] = useState(false);
  const [globalSpeed, setGlobalSpeed] = useState(15);

  const toggleGlobalPaused = () => {
    setGlobalPaused((prev) => !prev);
  };

  // 🎯 Control de pausa/reanudación al hacer click fuera
  const animationPaused = globalPaused || externalPaused;

  return (
    <ScrollCtx.Provider
      value={{
        globalPaused,
        globalSpeed,
        animationPaused,
        toggleGlobalPaused,
        setGlobalPaused,
        setGlobalSpeed,
      }}
    >
      {children}
    </ScrollCtx.Provider>
  );
}

export const useScrollControl = () => useContext(ScrollCtx);

const HoverCtx = React.createContext({
  hoverTS: null,
  setHoverTS: () => {},
  active: false,
  setActive: () => {},
  pos: { x: 0, y: 0 },
  setPosFromClient: () => {},
});

const MetricCtx = React.createContext({
  activeMetric: "O2",
  setActiveMetric: () => {},
});

const ChartsVisibilityCtx = React.createContext({
  showCharts: true,
  toggleCharts: () => {},
});

const PumpCtx = React.createContext({
  selectedPumps: [],
  setSelectedPumps: () => {},
});

export function ChartsVisibilityProvider({ children }) {
  const [showCharts, setShowCharts] = useState(true);
  const toggleCharts = () => {
    setShowCharts(prev => !prev);
  };

  useLayoutEffect(() => {
    if (typeof window === "undefined") return undefined;

    const frameIds = [];
    const timeoutIds = [];
    const queueFrame = () => {
      const frameId = window.requestAnimationFrame(() => {
        emitDashboardLayoutChange();
        const nextFrameId = window.requestAnimationFrame(emitDashboardLayoutChange);
        frameIds.push(nextFrameId);
      });
      frameIds.push(frameId);
    };

    emitDashboardLayoutChange();
    queueFrame();
    [80, 180, 320].forEach((delay) => {
      timeoutIds.push(window.setTimeout(emitDashboardLayoutChange, delay));
    });

    return () => {
      frameIds.forEach((frameId) => window.cancelAnimationFrame(frameId));
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [showCharts]);

  return (
    <ChartsVisibilityCtx.Provider value={{ showCharts, toggleCharts }}>
      {children}
    </ChartsVisibilityCtx.Provider>
  );
}

export const useChartsVisibility = () => useContext(ChartsVisibilityCtx);

export function CenterStatusIndicador({ center, generador, generadorApagado, compact = false }) {
  const status = useCenterStatus(center, generador);


  // Si el generador está apagado, ignoramos las fallas
  const adjustedStatus = generadorApagado
    ? {
        ...status,
        sinCom: 0,
        enFalla: 0,
        estado: "ok",
        color: "#00c853", // verde
      }
    : status;

  const tooltipText = `
    Bombas totales: ${adjustedStatus.total}
    Sin comunicación: ${adjustedStatus.sinCom}
    Falla de sensor: ${adjustedStatus.fallaSensor}
    Bombas en falla: ${adjustedStatus.enFalla}
    ${adjustedStatus.hasLowPressure ? "Sin presión de O2 en la línea" : ""}
  `;

  const Icon =
    adjustedStatus.estado === "error"
      ? ErrorOutline
      : adjustedStatus.estado === "warning"
      ? WarningAmber
      : CheckCircleIcon; // ícono verde de ok

  return (
    <CustomTooltip
      variant="pipeline"
      title={
        <pre
          style={{
            fontFamily: "inherit",
            fontSize: "min(0.75vw, 1.4vh)",
            margin: 0,
            lineHeight: 1.3,
          }}
        >
          {tooltipText}
        </pre>
      }
      color={"#c3c3c3"}
      arrow
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          marginLeft: "min(0.4vw, 0.8vh)",
          cursor: "help",
        }}
      >
        <Icon style={{ fontSize: compact ? "2vh" : "min(1.5vw, 2.5vh)", color: adjustedStatus.color }} />
      </div>
    </CustomTooltip>
  );
}

function getDashboardFallbackPageSize() {
  if (typeof window === "undefined") return 4;
  if (window.innerWidth <= 700) return 2;
  if (window.innerWidth <= 1200) return 3;
  return 4;
}

function useDashboardPageSize(bodyRef, totalCenters = 0) {
  const [pageSize, setPageSize] = useState(getDashboardFallbackPageSize);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return undefined;

    const measurePageSize = () => {
      const fallback = getDashboardFallbackPageSize();
      const body = bodyRef?.current;

      if (!body) {
        setPageSize((previous) => (previous === fallback ? previous : fallback));
        return;
      }

      const rows = Array.from(body.querySelectorAll(".center-row"));
      const rowHeights = rows
        .map((row) => row.getBoundingClientRect().height)
        .filter((height) => height > 0);
      const measuredRowHeight = rowHeights.length
        ? rowHeights.reduce((sum, height) => sum + height, 0) / rowHeights.length
        : 190;
      const styles = window.getComputedStyle(body);
      const gap = parseFloat(styles.rowGap || styles.gap || "8") || 8;
      const availableHeight = window.innerHeight - body.getBoundingClientRect().top - 16;
      const measuredPageSize = Math.max(
        1,
        Math.floor((availableHeight + gap) / (measuredRowHeight + gap))
      );
      const maxByWidth = window.innerWidth <= 700 ? 2 : window.innerWidth <= 1200 ? 4 : 8;
      const nextPageSize = Math.max(
        fallback,
        Math.min(maxByWidth, totalCenters || measuredPageSize, measuredPageSize)
      );

      setPageSize((previous) => (previous === nextPageSize ? previous : nextPageSize));
    };

    measurePageSize();
    window.addEventListener("resize", measurePageSize);
    window.addEventListener(DASHBOARD_LAYOUT_CHANGE_EVENT, measurePageSize);

    const resizeObserver =
      typeof ResizeObserver !== "undefined" && bodyRef?.current
        ? new ResizeObserver(measurePageSize)
        : null;
    resizeObserver?.observe(bodyRef.current);

    return () => {
      window.removeEventListener("resize", measurePageSize);
      window.removeEventListener(DASHBOARD_LAYOUT_CHANGE_EVENT, measurePageSize);
      resizeObserver?.disconnect();
    };
  }, [bodyRef, totalCenters]);

  return pageSize;
}

function getDashboardLegacyFallbackPageSize() {
  if (typeof window === "undefined") return 6;
  if (window.innerWidth <= 700) return 2;
  if (window.innerWidth <= 1100) return 3;
  if (window.innerWidth <= 1500) return 4;
  return 6;
}

function useDashboardLegacyPageSize(bodyRef, totalCenters = 0) {
  const [pageSize, setPageSize] = useState(getDashboardLegacyFallbackPageSize);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return undefined;

    const measurePageSize = () => {
      const fallback = getDashboardLegacyFallbackPageSize();
      const body = bodyRef?.current;

      if (!body) {
        setPageSize((previous) => (previous === fallback ? previous : fallback));
        return;
      }

      const styles = window.getComputedStyle(body);
      const gap = parseFloat(styles.columnGap || styles.gap || "8") || 8;
      const availableWidth = body.getBoundingClientRect().width || window.innerWidth;
      const firstCenter = body.querySelector(".itgDash__colCentro:not(.center-row)");
      const centerWidth = firstCenter?.getBoundingClientRect().width || Math.min(window.innerWidth * 0.135, window.innerHeight * 0.27);
      const measuredPageSize = Math.max(
        1,
        Math.floor((availableWidth + gap) / (centerWidth + gap))
      );
      const nextPageSize = Math.max(
        1,
        Math.min(totalCenters || measuredPageSize, measuredPageSize || fallback)
      );

      setPageSize((previous) => (previous === nextPageSize ? previous : nextPageSize));
    };

    measurePageSize();
    window.addEventListener("resize", measurePageSize);
    window.addEventListener(DASHBOARD_LAYOUT_CHANGE_EVENT, measurePageSize);

    const resizeObserver =
      typeof ResizeObserver !== "undefined" && bodyRef?.current
        ? new ResizeObserver(measurePageSize)
        : null;
    resizeObserver?.observe(bodyRef.current);

    return () => {
      window.removeEventListener("resize", measurePageSize);
      window.removeEventListener(DASHBOARD_LAYOUT_CHANGE_EVENT, measurePageSize);
      resizeObserver?.disconnect();
    };
  }, [bodyRef, totalCenters]);

  return pageSize;
}


/** Dashboard principal: 100% del contenedor y hover global */

export default function Dashboard({
  centersData = [],
  loading = false,
  error = null,
  expectedCenterCount = 0,
  onSelectCenter,
  setShowDashboard,
  version,
  translate,
}) {
  const labelsRevision = useLabelVersion();
  const rootRef = useRef(null);
  const centersGridRef = useRef(null);
  const dashboardBodyRef = useRef(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [showCenterMeta, setShowCenterMeta] = useState(true);

  const [hoverTS, setHoverTS] = useState(null);
  const [active, setActive] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const [activeMetric, setActiveMetric] = useState("O2");
  const [selectedPumps, setSelectedPumps] = useState([]);
  const [centerOrder, setCenterOrder] = useState(() => readStoredCenterOrder());
  const [draggingCenterKey, setDraggingCenterKey] = useState(null);
  const [dropCenterKey, setDropCenterKey] = useState(null);
  const [centerPage, setCenterPage] = useState(0);
  const [autoRotateCenters, setAutoRotateCenters] = useState(true);
  const [rotationPaused, setRotationPaused] = useState(false);
  const [dashboardViewMode, setDashboardViewMode] = useState(readStoredDashboardViewMode);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedCentro, setSelectedCentro] = useState(() => getStoredCentroKey());
  const [isSlowLoading, setIsSlowLoading] = useState(false);

  const isReorderingCenters = Boolean(draggingCenterKey);
  const isLegacyDashboardView = dashboardViewMode === "legacy";
  const isSummaryDashboardView = dashboardViewMode === "summary-v3";

  const toggleDrawer = (newOpen) => () => {
    setOpenDrawer(newOpen);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(DASHBOARD_VIEW_MODE_KEY, dashboardViewMode);
  }, [dashboardViewMode]);

  useEffect(() => {
    if (!loading) {
      setIsSlowLoading(false);
      return undefined;
    }

    const timeout = window.setTimeout(() => setIsSlowLoading(true), 10000);
    return () => window.clearTimeout(timeout);
  }, [loading]);

  const setPosFromClient = (clientX, clientY) => {
    if (!rootRef.current) return;
    const r = rootRef.current.getBoundingClientRect();
    setPos({ x: clientX - r.left, y: clientY - r.top });
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedCenter(null);
  };

  useEffect(() => {
    if (!centersGridRef.current) return;
    centersGridRef.current.scrollLeft = 0;
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleDashboardLayoutChange = () => {
      setCenterPage(0);
      if (centersGridRef.current) centersGridRef.current.scrollLeft = 0;
      if (dashboardBodyRef.current) dashboardBodyRef.current.scrollTop = 0;
    };

    window.addEventListener(DASHBOARD_LAYOUT_CHANGE_EVENT, handleDashboardLayoutChange);
    return () => {
      window.removeEventListener(DASHBOARD_LAYOUT_CHANGE_EVENT, handleDashboardLayoutChange);
    };
  }, []);

  const sanitizedCenters = useMemo(
    () =>
      centersData.map((center) => ({
        center,
        sanitized: sanitizeDashboardCenter(center),
      })),
    [centersData]
  );

  const visibleCenters = useMemo(
    () => sanitizedCenters.map((item) => item.sanitized).filter(Boolean),
    [sanitizedCenters]
  );

  const centersWithoutData = useMemo(
    () =>
      sanitizedCenters
        .filter((item) => item.center && !item.sanitized)
        .map((item) => item.center),
    [sanitizedCenters]
  );

  const centersWithoutDataGroups = useMemo(
    () => groupCentersWithoutData(centersWithoutData),
    [centersWithoutData, labelsRevision] // eslint-disable-line react-hooks/exhaustive-deps -- La versión invalida labels del resolvedor en memoria.
  );
  /*
    return name && name !== "â€”";
  });

  */

  useEffect(() => {
    setCenterOrder((previousOrder) => {
      const nextOrder = buildOrderedCenterKeys(visibleCenters, previousOrder);
      return JSON.stringify(previousOrder) === JSON.stringify(nextOrder)
        ? previousOrder
        : nextOrder;
    });
  }, [visibleCenters]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      DASHBOARD_CENTER_ORDER_KEY,
      JSON.stringify(centerOrder)
    );
  }, [centerOrder]);

  const orderedVisibleCenters = useMemo(
    () => orderCentersByKeys(visibleCenters, centerOrder),
    [visibleCenters, centerOrder]
  );

  const centersPerPage = useDashboardPageSize(
    dashboardBodyRef,
    orderedVisibleCenters.length || expectedCenterCount || visibleCenters.length
  );
  const legacyCentersPerPage = useDashboardLegacyPageSize(
    dashboardBodyRef,
    orderedVisibleCenters.length || expectedCenterCount || visibleCenters.length
  );
  const activeCentersPerPage = isSummaryDashboardView
    ? orderedVisibleCenters.length || 1
    : isLegacyDashboardView
    ? legacyCentersPerPage
    : centersPerPage;

  const pageCount = Math.max(1, Math.ceil(orderedVisibleCenters.length / activeCentersPerPage));
  const currentPage = Math.min(centerPage, pageCount - 1);
  const pagedCenters = useMemo(() => {
    const start = currentPage * activeCentersPerPage;
    return orderedVisibleCenters.slice(start, start + activeCentersPerPage);
  }, [orderedVisibleCenters, currentPage, activeCentersPerPage]);

  useEffect(() => {
    setCenterPage(0);
    if (centersGridRef.current) centersGridRef.current.scrollLeft = 0;
    if (dashboardBodyRef.current) dashboardBodyRef.current.scrollTop = 0;
  }, [dashboardViewMode, activeCentersPerPage]);

  useEffect(() => {
    setCenterPage((previousPage) => Math.min(previousPage, pageCount - 1));
  }, [pageCount]);

  useEffect(() => {
    if (!autoRotateCenters || rotationPaused || isReorderingCenters || pageCount <= 1) return;

    const interval = window.setInterval(() => {
      setCenterPage((previousPage) => (previousPage + 1) % pageCount);
    }, 12000);

    return () => window.clearInterval(interval);
  }, [autoRotateCenters, rotationPaused, isReorderingCenters, pageCount]);

  const goToPreviousPage = () => {
    setCenterPage((previousPage) => (previousPage - 1 + pageCount) % pageCount);
  };

  const goToNextPage = () => {
    setCenterPage((previousPage) => (previousPage + 1) % pageCount);
  };

  const skeletonSlots = Math.max(
    0,
    Math.min(activeCentersPerPage, expectedCenterCount || visibleCenters.length || 1) - pagedCenters.length
  );
  const skeletonCount = loading ? Math.min(2, skeletonSlots) : 0;
  const loadedCenterCount = Math.min(
    centersData.length,
    expectedCenterCount || centersData.length
  );
  const loadingStatusText = isSlowLoading
    ? t(translate, "dashboard_centers_slow_loading", "La carga está tardando más de lo normal. Los centros disponibles se mostrarán apenas respondan.")
    : expectedCenterCount > 0
      ? `${t(translate, "dashboard_loading_centers", "Cargando centros Nanoox")}… ${loadedCenterCount} / ${expectedCenterCount}`
      : `${t(translate, "dashboard_loading_centers", "Cargando centros Nanoox")}…`;
  const centersForSelectedView = pagedCenters;

  const finishCenterDrag = () => {
    setDraggingCenterKey(null);
    setDropCenterKey(null);
  };

  const handleCenterDragStart = (centerKey) => (event) => {
    if (!centerKey) return;

    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", centerKey);
    setDraggingCenterKey(centerKey);
    setDropCenterKey(centerKey);
  };

  const handleCenterDragOver = (centerKey) => (event) => {
    event.preventDefault();
    if (!draggingCenterKey || draggingCenterKey === centerKey) return;

    event.dataTransfer.dropEffect = "move";
    setDropCenterKey(centerKey);
  };

  const handleCenterDrop = (centerKey) => (event) => {
    event.preventDefault();
    const draggedKey =
      draggingCenterKey || event.dataTransfer.getData("text/plain");

    if (!draggedKey || draggedKey === centerKey) {
      finishCenterDrag();
      return;
    }

    setCenterOrder((previousOrder) =>
      moveCenterKey(
        buildOrderedCenterKeys(visibleCenters, previousOrder),
        draggedKey,
        centerKey
      )
    );
    finishCenterDrag();
  };

  return (
    <div
      className={`itgDash ${isReorderingCenters ? "itgDash--dragging" : ""}`}
      ref={rootRef}
    >
      <HoverCtx.Provider value={{ hoverTS, setHoverTS, active, setActive, pos, setPosFromClient }}>
        <MetricCtx.Provider value={{ activeMetric, setActiveMetric }}>
          {/* <GlobalTooltip /> */}
          <ChartsVisibilityProvider>
            <PumpCtx.Provider value={{ selectedPumps, setSelectedPumps }}>
              
              <header className="itgDash__header">
                <div className="itgDash__statsWrap">
                  {/* <h1>Monitoreo Nanoox</h1> */}

                  {/* <button
                    onClick={() => exportNanooxReportDark(centersData)}
                    style={{
                      marginLeft: "1vw",
                      padding: "0.5vh 1vw",
                      backgroundColor: "#e84e0e",
                      color: "#fff",
                      border: "none",
                      borderRadius: "min(0.4vw, 0.8vh)",
                      cursor: "pointer",
                    }}
                  >
                    Generar Reporte PDF
                  </button>
                   */}
                  <div className="itgDash__headerStart">
                    <img
                      src={Logo}
                      alt="ITG"
                      className="logo-itg"
                      onClick={toggleDrawer(true)}
                      onMouseEnter={toggleDrawer(true)}
                      onMouseLeave={toggleDrawer(false)}
                      role="button"
                      tabIndex={0}
                    />
                  </div>

                  {/* Promedios por centro */}
                  <CentersStats centersData={visibleCenters} translate={translate} />

                  <div className="itgDash__headerEnd">
                    {!isLegacyDashboardView && !isSummaryDashboardView ? <ChartsToggleButton translate={translate} /> : null}
                    {!isSummaryDashboardView ? <MetricSelector /> : null}
                    <DashboardViewSelector
                      value={dashboardViewMode}
                      onChange={setDashboardViewMode}
                      translate={translate}
                    />
                    <img  src={LogoNanoox} alt="ITG" className="logo-itg" onClick={()=>{setShowDashboard(false)}} />
                  </div>

                </div>
              </header>
              <ScrollProvider externalPaused={isReorderingCenters}>
                {!isSummaryDashboardView ? (
                  <DashboardPager
                    currentPage={currentPage}
                    pageCount={pageCount}
                    totalCenters={orderedVisibleCenters.length}
                    centersPerPage={activeCentersPerPage}
                    autoRotate={autoRotateCenters}
                    rotationPaused={rotationPaused || isReorderingCenters}
                    onPrevious={goToPreviousPage}
                    onNext={goToNextPage}
                    onToggleAutoRotate={() => setAutoRotateCenters((value) => !value)}
                    translate={translate}
                  />
                ) : null}
                <div className={`itgDash__grid ${isSummaryDashboardView ? "itgDash__grid--summaryV3" : ""}`} ref={centersGridRef}>
                  <main
                    className={`${isSummaryDashboardView ? "summary-dashboard-v3-shell" : isLegacyDashboardView ? "dashboard-legacy" : "dashboard-body"} itgDash__grid_left`}
                    ref={dashboardBodyRef}
                    onMouseEnter={() => setRotationPaused(true)}
                    onMouseLeave={() => setRotationPaused(false)}
                    onFocus={() => setRotationPaused(true)}
                    onBlur={() => setRotationPaused(false)}
                  >
                    {isSummaryDashboardView ? (
                      <SummaryDashboardV3View
                        centers={orderedVisibleCenters}
                        loading={loading}
                        onSelectCenter={onSelectCenter}
                        translate={translate}
                      />
                    ) : (
                    <>
                    {/* {loading ? (
                      <div className={`dashboard-loading-status${isSlowLoading ? " dashboard-loading-status--slow" : ""}`} role="status" aria-live="polite">
                        <span className="dashboard-loading-status__indicator" aria-hidden="true" />
                        <span>{loadingStatusText}</span>
                      </div>
                    ) : null} */}
                    {/* {!loading && centersWithoutData.length > 0 && orderedVisibleCenters.length > 0 ? (
                      <div className="dashboard-centers-note" role="status" aria-live="polite">
                        <span>{`${centersWithoutData.length} ${t(translate, "dashboard_centers_without_data_short", "centro(s) sin datos de jaulas")}`}</span>
                        <CentersWithoutDataGroups groups={centersWithoutDataGroups} translate={translate} />
                      </div>
                    ) : null} */}
                    {/* {centersData.map((center, i) => { */}
                    {centersForSelectedView
                      .filter(() => true)
                      /*
                        const fullId =
                          center?.centro ||
                          center?.modulos?.[0]?.cans?.[0]?.centro_id;

                        if (!fullId) return false;

                        const name = fullId.split("-").pop().trim();
                        return name && name !== "—";
                      })
                      */
                      .map((center, i) => {
                      const centerKey = getCenterOrderKey(center) || `center-${i}`;
                      const centerName = labelId(getCenterFullId(center), "rac", getCenterDisplayName(center));
                      const generador = center?.generadores 
                      const umbrales  = center?.umbrales
                      const hasAnyPressureHysteresis =
                        Array.isArray(center.modulos) &&
                        center.modulos.some(({ cans = [] }) =>
                          cans.some(
                            ({ presion, bombas = [] }) =>
                              presion !== null &&
                              presion < 3.5 &&
                              bombas.some((bomba) => Boolean(bomba?.aviot_status))
                          )
                        );
                      const generadorApagado =
                        (generador?.partida === 60 && generador?.detencion === 100) ||
                        generador?.hf_status === 60;
                      const hasGeneratorData = Array.isArray(generador)
                        ? generador.length > 0
                        : Boolean(generador && Object.keys(generador).length);



                      /*
                      function getCentroName(center) {
                        const fullId = center?.centro || center?.modulos?.[0]?.cans?.[0]?.centro_id;
                        if (!fullId) return "—";
                        const parts = fullId.split("-");
                        return parts[parts.length - 1].trim();
                      }
                      */
                      const canOpenCenter =
                        Boolean(center?.centro) &&
                        Boolean(center?.area) &&
                        Boolean(center?.region);

                      const handleGoToCenter = () => {
                        if (!canOpenCenter) return;

                        onSelectCenter?.({
                          centro: center.centro,
                          area: center.area,
                          region: center.region,
                        });
                      };

                      const handleOpenHistory = () => {
                        setSelectedCenter(center);
                        setOpenModal(true);
                      };

                      const minThreshold = umbrales?.setpoin_on || umbrales?.[0]?.Min || "";
                      const maxThreshold = umbrales?.setpoin_off || umbrales?.[0]?.Max || "";
                      const measurementIsStale = isMeasurementStale(center?.hora, 20);
                      const staleElapsedText = formatElapsedSinceMeasurement(center?.hora);

                      if (isLegacyDashboardView) {
                        return (
                          <div
                            key={centerKey}
                            className={[
                              "itgDash__colCentro",
                              draggingCenterKey === centerKey ? "is-dragging" : "",
                              dropCenterKey === centerKey && draggingCenterKey !== centerKey
                                ? "is-drop-target"
                                : "",
                            ].filter(Boolean).join(" ")}
                            onDragOver={handleCenterDragOver(centerKey)}
                            onDrop={handleCenterDrop(centerKey)}
                          >
                            <div className="itgDash__colTitle itgDash__colTitle--compact">
                              <div className="itgDash__titleTop">
                                <div className="itgDash__titleMain">
                                  <span className="itgDash__titleName">{centerName}</span>
                                  <CenterStatusIndicador
                                    center={center}
                                    generador={generador}
                                    hasLowPressure={hasAnyPressureHysteresis}
                                    generadorApagado={generadorApagado}
                                  />
                                </div>

                                <div className="itgDash__titleActions">
                                  <div
                                    className="itgDash__dragHandle"
                                    draggable
                                    onDragStart={handleCenterDragStart(centerKey)}
                                    onDragEnd={finishCenterDrag}
                                    aria-label={t(translate, "dashboard_drag_center", "Reordenar centro")}
                                    title={t(translate, "dashboard_drag_center", "Reordenar centro")}
                                  >
                                    <DragIndicatorRoundedIcon style={{ fontSize: "min(0.62vw, 1.2vh)" }} />
                                  </div>
                                  <button
                                    type="button"
                                    className="itgDash__actionBtn itgDash__actionBtn--icon"
                                    onClick={handleGoToCenter}
                                    disabled={!canOpenCenter}
                                    aria-label={t(translate, "ver_centro", "Ver centro")}
                                    title={t(translate, "ver_centro", "Ver centro")}
                                  >
                                    <LoupeIcon style={{ fontSize: "min(0.62vw, 1.2vh)" }} />
                                  </button>

                                  <button
                                    type="button"
                                    className="itgDash__actionBtn itgDash__actionBtn--icon"
                                    onClick={handleOpenHistory}
                                    aria-label={t(translate, "history_control_title", "Historial de Control")}
                                    title={t(translate, "history_control_title", "Historial de Control")}
                                  >
                                    <EventNoteIcon style={{ fontSize: "min(0.62vw, 1.2vh)" }} />
                                  </button>
                                </div>
                              </div>

                              <div className="itgDash__titleSummary itgDash__titleSummary--compact">
                                {hasGeneratorData ? (
                                  <div className="itgDash__summaryItem itgDash__summaryItem--generator">
                                    <span className="itgDash__summaryLabel">
                                      {t(translate, "generator", "Generador")}
                                    </span>
                                    <div className="itgDash__summaryGenerator">
                                      <GeneratorsStatus
                                        items={generador}
                                        translate={translate}
                                        wrap
                                        compact
                                        gap="0.6vw"
                                        sizeVh={3.6}
                                      />
                                    </div>
                                  </div>
                                ) : null}

                                {measurementIsStale ? (
                                  <div className="itgDash__staleAlert">
                                    <WarningAmber style={{ fontSize: "min(0.82vw, 1.55vh)" }} />
                                    <span>
                                      {t(translate, "dashboard_stale_measurement", "Alerta: no se reciben datos")} {staleElapsedText}
                                    </span>
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            <Modal
                              open={openModal && getCenterOrderKey(selectedCenter) === centerKey}
                              onClose={handleCloseModal}
                              hideBackdrop
                              disableAutoFocus
                              disableEnforceFocus
                              disableRestoreFocus
                              className="itgDash__historyModalRoot"
                              aria-labelledby="modal-title"
                              aria-describedby="modal-description"
                            >
                              <Box className="itgDash__historyModalPanel" sx={{ pointerEvents: 'auto' }}>
                                <div className="itgDash__historyModalHead">
                                  <div>
                                    <span className="itgDash__historyModalEyebrow">
                                      {t(translate, "history_control_title", "Historial de Control")}
                                    </span>
                                    <h2 id="modal-title">
                                      {labelId(getCenterFullId(selectedCenter), "rac", getCenterDisplayName(selectedCenter))}
                                    </h2>
                                  </div>
                                  <button
                                    type="button"
                                    className="itgDash__historyModalClose"
                                    onClick={handleCloseModal}
                                    aria-label={t(translate, "dashboard_close_history", "Cerrar historial")}
                                  >
                                    x
                                  </button>
                                </div>
                                <DashboardHistoryContent
                                  selectedCenter={selectedCenter}
                                  translate={translate}
                                />
                              </Box>
                            </Modal>

                            <AutoScrollContainer>
                              {Array.isArray(center?.modulos)
                                ? center.modulos.map((mod, j) => (
                                    <ModuleCard
                                      key={mod.id ?? j}
                                      modulo={mod}
                                      generador={generador}
                                      generadorApagado={generadorApagado}
                                      translate={translate}
                                    />
                                  ))
                                : null}
                            </AutoScrollContainer>
                          </div>
                        );
                      }
                      
                      return(
                        <CenterRow
                          key={centerKey}
                          center={center}
                          centerKeyValue={centerKey}
                          centerName={centerName}
                          generador={generador}
                          generadorApagado={generadorApagado}
                          hasAnyPressureHysteresis={hasAnyPressureHysteresis}
                          hasGeneratorData={hasGeneratorData}
                          minThreshold={minThreshold}
                          maxThreshold={maxThreshold}
                          measurementIsStale={measurementIsStale}
                          staleElapsedText={staleElapsedText}
                          canOpenCenter={canOpenCenter}
                          translate={translate}
                          draggingCenterKey={draggingCenterKey}
                          dropCenterKey={dropCenterKey}
                          onDragOver={handleCenterDragOver(centerKey)}
                          onDrop={handleCenterDrop(centerKey)}
                          onDragStart={handleCenterDragStart(centerKey)}
                          onDragEnd={finishCenterDrag}
                          onGoToCenter={handleGoToCenter}
                          onOpenHistory={handleOpenHistory}
                        >
                              {/* {showCenterMeta ? (
                              <div className="itgDash__titleMetaRow"> 
                                <div className="itgDash__summaryItem itgDash__summaryItem--measurement">
                                  <span className="itgDash__summaryLabel">
                                    {t(translate, "ultima_medicion", "Ultima Medicion")}
                                  </span>
                                  <span className="itgDash__summaryValue">
                                    {center?.hora || "—"}
                                  </span>
                                </div>

                                <div className="itgDash__summaryItem itgDash__summaryItem--threshold">
                                  <span className="itgDash__summaryLabel">
                                    {t(translate, "umbral", "Umbral de Inyeccion O2")} [mg/L]
                                  </span>
                                  <span className="itgDash__summaryValue">
                                    {t(translate, "min", "Min")}: {minThreshold}  {t(translate, "max", "Max")}: {maxThreshold}
                                  </span>
                                </div>
                              </div>
                              ) : null} */}
                        <Modal
                          open={openModal && getCenterOrderKey(selectedCenter) === centerKey}
                          onClose={handleCloseModal}
                          hideBackdrop
                          disableAutoFocus
                          disableEnforceFocus
                          disableRestoreFocus
                          className="itgDash__historyModalRoot"
                          aria-labelledby="modal-title"
                          aria-describedby="modal-description"
                        >
                          <Box
                            className="itgDash__historyModalPanel"
                            sx={{
                              pointerEvents: 'auto',
                            }}
                          >
                            <div className="itgDash__historyModalHead">
                              <div>
                                <span className="itgDash__historyModalEyebrow">
                                  {t(translate, "history_control_title", "Historial de Control")}
                                </span>
                                <h2 id="modal-title">
                                  {labelId(getCenterFullId(selectedCenter), "rac", getCenterDisplayName(selectedCenter))}
                                </h2>
                              </div>
                              <button
                                type="button"
                                className="itgDash__historyModalClose"
                                onClick={handleCloseModal}
                                aria-label={t(translate, "dashboard_close_history", "Cerrar historial")}
                              >
                                x
                              </button>
                            </div>
                            <DashboardHistoryContent
                              selectedCenter={selectedCenter}
                              translate={translate}
                            />
                          </Box>
                        </Modal>
                        {/* Módulos */}
                        </CenterRow>
                      )
                    })}
                    {!loading && error && orderedVisibleCenters.length === 0 ? (
                      <div className="dashboard-empty-state dashboard-empty-state--error">
                        {t(translate, "dashboard_centers_load_error", "No fue posible cargar los centros Nanoox. Intente nuevamente.")}
                      </div>
                    ) : null}
                    {!loading && !error && orderedVisibleCenters.length === 0 ? (
                      <div className="dashboard-empty-state">
                        {expectedCenterCount === 0
                          ? t(translate, "dashboard_no_configured_centers", "No hay centros Nanoox activos configurados.")
                          : centersWithoutData.length > 0
                            ? (
                              <>
                                <div>{t(translate, "dashboard_centers_without_data", "Los centros consultados respondieron sin datos de jaulas")}</div>
                                <CentersWithoutDataGroups groups={centersWithoutDataGroups} translate={translate} />
                              </>
                            )
                            : t(translate, "dashboard_no_active_cages_centers", "No hay centros con jaulas activas para mostrar.")}
                      </div>
                    ) : null}
                    {loading && skeletonCount > 0
                      ? Array.from({ length: skeletonCount }).map((_, index) => (
                          <DashboardCenterSkeleton key={`dash-skeleton-${index}`} />
                        ))
                      : null}
                    </>
                    )}
                  </main>

                </div>
              </ScrollProvider>

              <DrawerNanoox
                open={openDrawer}
                toggleDrawer={toggleDrawer}
                onSelectCenter={onSelectCenter}
                version={version}
                selectedCentro={selectedCentro}
                setSelectedCentro={setSelectedCentro}
                translate={translate}
              />

            </PumpCtx.Provider>
          </ChartsVisibilityProvider>
        </MetricCtx.Provider>
      </HoverCtx.Provider>
    </div>
  );
}

function DashboardPager({
  currentPage,
  pageCount,
  totalCenters,
  centersPerPage,
  autoRotate,
  rotationPaused,
  onPrevious,
  onNext,
  onToggleAutoRotate,
  translate,
}) {
  if (totalCenters <= centersPerPage && pageCount <= 1) return null;

  return (
    <div className="dashboard-pager" aria-label={t(translate, "dashboard_centers_navigation", "Navegacion de centros")}>
      <button
        type="button"
        className="dashboard-pager__button"
        onClick={onPrevious}
        aria-label={t(translate, "dashboard_previous_centers", "Centros anteriores")}
      >
        &lt;
      </button>
      <div className="dashboard-pager__status">
        <strong>
          {t(translate, "page_of", "Pagina {page} de {total}")
            .replace("{page}", currentPage + 1)
            .replace("{total}", pageCount)}
        </strong>
        <span>
          {totalCenters} {t(translate, "dashboard_active_centers_lower", "centros activos")} | {centersPerPage} {t(translate, "dashboard_visible_lower", "visibles")}
          {autoRotate
            ? rotationPaused
              ? ` | ${t(translate, "dashboard_rotation_paused", "rotacion pausada")}`
              : ` | ${t(translate, "dashboard_auto_rotation", "rotacion automatica")}`
            : ` | ${t(translate, "manual", "manual")}`}
        </span>
      </div>
      <button
        type="button"
        className="dashboard-pager__toggle"
        onClick={onToggleAutoRotate}
      >
        {autoRotate ? t(translate, "dashboard_pause", "Pausar") : t(translate, "auto", "Auto")}
      </button>
      <button
        type="button"
        className="dashboard-pager__button"
        onClick={onNext}
        aria-label={t(translate, "dashboard_next_centers", "Centros siguientes")}
      >
        &gt;
      </button>
    </div>
  );
}

function DashboardViewSelector({ value, onChange, translate }) {
  const options = [
    { value: "current", label: t(translate, "dashboard_view_current", "Actual"), icon: ViewStreamRoundedIcon },
    { value: "legacy", label: t(translate, "dashboard_view_previous", "Anterior"), icon: ViewColumnRoundedIcon },
    { value: "summary-v3", label: t(translate, "dashboard_view_summary_v3", "Resumen V3"), icon: DashboardRoundedIcon },
  ];

  return (
    <div className="dashboard-view-switch" aria-label={t(translate, "dashboard_change_view", "Cambiar vista del dashboard")}>
      {options.map((option) => {
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            type="button"
            className={value === option.value ? "active" : ""}
            onClick={() => onChange(option.value)}
            aria-label={option.label}
            aria-pressed={value === option.value}
            title={option.label}
          >
            <Icon className="dashboard-view-switch__icon" aria-hidden="true" />
            <span className="dashboard-view-switch__label">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function DashboardHistoryContent({ selectedCenter, translate }) {
  return (
    <div id="modal-description">
      {selectedCenter?.historial_control?.length ? (
        <div className="itgDash__historyModalList">
          {selectedCenter.historial_control.map((item, index) => {
            const entry = formatHistoryEntry(item, translate);
            const tone = getHistoryTone(item?.accion, item?.descripcion, item?.msg);

            return (
              <div key={`${entry.fecha}-${index}`} className="itgDash__historyModalItem">
                <div className="itgDash__historyModalInfo">
                  {entry.preferCombinedMessage ? (
                    <span className="itgDash__historyText">{entry.message}</span>
                  ) : entry.isStructured ? (
                    <>
                      {entry.responsable ? (
                        <span className="itgDash__historyUser">{entry.responsable}</span>
                      ) : null}
                      {entry.accion ? (
                        <span className="itgDash__historyAction" style={{ color: tone }}>
                          {entry.accion}
                        </span>
                      ) : null}
                      {entry.descripcion ? (
                        <span className="itgDash__historyText">{entry.descripcion}</span>
                      ) : null}
                      {entry.ubicacion ? (
                        <span className="itgDash__historyText">{entry.ubicacion}</span>
                      ) : null}
                    </>
                  ) : (
                    <span className="itgDash__historyText">{entry.message}</span>
                  )}
                </div>
                {entry.fecha ? (
                  <span className="itgDash__historyDate">{entry.fecha}</span>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <p>{t(translate, "history_no_records_center", "No hay registros de control para este centro.")}</p>
      )}
    </div>
  );
}

function CenterRow({
  center,
  centerName,
  centerKeyValue,
  generador,
  generadorApagado,
  hasAnyPressureHysteresis,
  hasGeneratorData,
  minThreshold,
  maxThreshold,
  measurementIsStale,
  staleElapsedText,
  canOpenCenter,
  translate,
  draggingCenterKey,
  dropCenterKey,
  onDragOver,
  onDrop,
  onDragStart,
  onDragEnd,
  onGoToCenter,
  onOpenHistory,
  children,
}) {
  return (
    <section
      className={[
        "center-row",
        "itgDash__colCentro",
        draggingCenterKey === centerKeyValue ? "is-dragging" : "",
        dropCenterKey === centerKeyValue && draggingCenterKey !== centerKeyValue
          ? "is-drop-target"
          : "",
      ].filter(Boolean).join(" ")}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <CenterSummary
        center={center}
        centerName={centerName}
        generador={generador}
        generadorApagado={generadorApagado}
        hasAnyPressureHysteresis={hasAnyPressureHysteresis}
        minThreshold={minThreshold}
        maxThreshold={maxThreshold}
        measurementIsStale={measurementIsStale}
        staleElapsedText={staleElapsedText}
        canOpenCenter={canOpenCenter}
        translate={translate}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onGoToCenter={onGoToCenter}
        onOpenHistory={onOpenHistory}
        hasGeneratorData={hasGeneratorData}
      />

      <SensorCardsGrid
        modulos={center?.modulos}
        lastMeasurement={center?.hora}
        minThreshold={minThreshold}
        maxThreshold={maxThreshold}
        translate={translate}
        generador={generador}
        generadorApagado={generadorApagado}
      />

      {children}
    </section>
  );
}

function CenterSummary({
  center,
  centerName,
  generador,
  generadorApagado,
  hasAnyPressureHysteresis,
  hasGeneratorData,
  minThreshold,
  maxThreshold,
  measurementIsStale,
  staleElapsedText,
  canOpenCenter,
  translate,
  onDragStart,
  onDragEnd,
  onGoToCenter,
  onOpenHistory,
}) {
  const averages = computeModuleAverages(toArray(center?.modulos));
  const o2Values = averages.map((item) => item.o2).filter(isNum);
  const o2Average = o2Values.length
    ? o2Values.reduce((sum, value) => sum + value, 0) / o2Values.length
    : null;

  return (
    <div className="center-summary">
      <div className="center-summary__identity">
        <div className="center-summary__nameWrap">
          <span className="itgDash__titleName">{centerName}</span>
          <CenterStatusIndicador
            center={center}
            generador={generador}
            hasLowPressure={hasAnyPressureHysteresis}
            generadorApagado={generadorApagado}
            compact
          />
        </div>

        <div className="center-summary__actions">
          <div
            className="itgDash__dragHandle"
            draggable
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            aria-label={t(translate, "dashboard_drag_center", "Reordenar centro")}
            title={t(translate, "dashboard_drag_center", "Reordenar centro")}
          >
            <DragIndicatorRoundedIcon style={{ fontSize: "min(0.98vw, 1.2vh)" }} />
          </div>
          <button
            type="button"
            className="itgDash__actionBtn itgDash__actionBtn--icon"
            onClick={onGoToCenter}
            disabled={!canOpenCenter}
            aria-label={t(translate, "ver_centro", "Ver centro")}
            title={t(translate, "ver_centro", "Ver centro")}
          >
            <LoupeIcon style={{ fontSize: "min(0.48vw, 0.9vh)" }} />
          </button>

          <button
            type="button"
            className="itgDash__actionBtn itgDash__actionBtn--icon"
            onClick={onOpenHistory}
            aria-label={t(translate, "history_control_title", "Historial de Control")}
            title={t(translate, "history_control_title", "Historial de Control")}
          >
            <EventNoteIcon style={{ fontSize: "min(0.48vw, 0.9vh)" }} />
          </button>
        </div>
      </div>
      <div className="center-summary__meta">
          <span>
            {t(translate, "ultima_medicion", "Última medición")}:
            <strong>{formatCenterStatsMeasurement(center?.hora)}</strong>
          </span>
          <span>
            {t(translate, "umbral", "Umbral O2")}: <strong>min {minThreshold || "-"} / max {maxThreshold || "-"}</strong>
          </span>
      </div>

      <GeneratorCard
        generador={generador}
        hasGeneratorData={hasGeneratorData}
        translate={translate}
      />

      {measurementIsStale ? (
        <div className="itgDash__staleAlert">
          <WarningAmber style={{ fontSize: "min(0.82vw, 1.55vh)" }} />
          <span>
            {t(translate, "dashboard_stale_measurement", "Alerta: no se reciben datos")} {staleElapsedText}
          </span>
        </div>
      ) : null}
    </div>
  );
}

function GeneratorCard({ generador, hasGeneratorData, translate }) {
  const generatorCount = Array.isArray(generador)
    ? generador.filter(Boolean).length
    : generador && typeof generador === "object"
      ? Object.keys(generador).length > 1 && !("hf_status" in generador) && !("plc_status" in generador)
        ? Object.values(generador).filter(Boolean).length
        : 1
      : 0;
  const hasMultipleGenerators = generatorCount > 1;

  return (
    <div className={`generator-status ${hasMultipleGenerators ? "generator-status--multi" : ""}`}>
      {hasGeneratorData ? (
        <div className="itgDash__summaryGenerator">
          <GeneratorsStatus
            items={generador}
            translate={translate}
            wrap={false}
            compact
            gap={hasMultipleGenerators ? 3 : "0.35vw"}
            sizeVh={hasMultipleGenerators ? 2.1 : 2.55}
          />
        </div>
      ) : (
        <span className="generator-status__empty">{t(translate, "dashboard_no_generator", "Sin generador")}</span>
      )}
    </div>
  );
}

function SensorCardsGrid({ modulos, lastMeasurement, minThreshold, maxThreshold, translate, generador, generadorApagado }) {
  const { showCharts } = useChartsVisibility();
  const moduleList = toArray(modulos);
  const isTwoModuleLayout = moduleList.length === 2;
  const hasThreeCanFourPumpModule = moduleList.some((modulo) => moduleUsesParitySplit(modulo));
  const inheritParitySplit = isTwoModuleLayout && hasThreeCanFourPumpModule;
  const moduleColumns = isTwoModuleLayout
    ? moduleList.map((modulo) => getModuleGridTrack(modulo, showCharts)).join(" ")
    : undefined;

  return (
    <div
      className={`sensor-cards-grid ${isTwoModuleLayout ? "sensor-cards-grid--two-modules" : ""} ${showCharts ? "" : "sensor-cards-grid--no-charts"}`}
      style={moduleColumns ? { "--sensor-module-columns": moduleColumns } : undefined}
    >
      {moduleList.length ? moduleList.map((modulo, index) => (
        <ModuleColumn
          key={modulo?.id ?? index}
          modulo={modulo}
          moduleIndex={index}
          moduleCount={moduleList.length}
          lastMeasurement={lastMeasurement}
          minThreshold={minThreshold}
          maxThreshold={maxThreshold}
          translate={translate}
          showCharts={showCharts}
          generador={generador}
          generadorApagado={generadorApagado}
          inheritParitySplit={inheritParitySplit}
        />
      )) : null}
    </div>
  );
}

function moduleUsesParitySplit(modulo) {
  const cans = toArray(modulo?.cans);
  return cans.length === 3 && cans.some((can) => toArray(can?.bombas).length === 4);
}

function getModuleGridTrack(modulo, showCharts = true) {
  const cans = toArray(modulo?.cans);
  const visualUnits = cans.reduce((sum, can) => {
    const pumpCount = toArray(can?.bombas).length;
    if (!pumpCount) return sum;
    return sum + (pumpCount <= 2 ? (showCharts ? 4 : 2.5) : pumpCount);
  }, 0);
  const weight = Math.max(showCharts ? 4 : 2.5, visualUnits);
  const minWidth = showCharts
    ? weight <= 4 ? "11.5vw" : weight <= 6 ? "17.7vw" : "25vw"
    : weight <= 3 ? "7.2vw" : weight <= 6 ? "11.5vw" : "17vw";

  return `minmax(${minWidth}, ${weight}fr)`;
}

function ModuleColumn({
  modulo,
  moduleIndex = 0,
  moduleCount = 1,
  showCharts = true,
  generador,
  generadorApagado,
  translate,
  inheritParitySplit = false
}) {
  const cans = toArray(modulo?.cans);
  const firstCan = cans[0];
  const moduleName = firstCan?.modulo || modulo?.id || "-";
  const splitCagesByAliasParity = inheritParitySplit || moduleUsesParitySplit(modulo);
  const canColumns = moduleCount === 2
    ? cans.map((can) => getCanGridTrack(can, showCharts)).join(" ")
    : undefined;

  return (
    <section className="module-column">
      <aside className="module-column__label">
        <strong>{t(translate, "modulo", "Modulo")} {moduleName}</strong>
      </aside>
      <div className="module-column__content">
        <div
          className="module-column__body"
          style={canColumns ? { "--module-can-columns": canColumns } : undefined}
        >
          {cans.map((can, index) => (
            <CanRow
              key={can?.can_id ?? index}
              can={can}
              moduleId={modulo?.id ?? moduleName}
              showCharts={showCharts}
              generador={generador}
              generadorApagado={generadorApagado}
              splitByAliasParity={splitCagesByAliasParity}
              translate={translate}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function getCanGridTrack(can, showCharts = true) {
  const pumpCount = toArray(can?.bombas).length;
  if (!showCharts) {
    if (pumpCount <= 2) return "minmax(4.9vw, 0.42fr)";
    if (pumpCount <= 4) return "minmax(8.8vw, 0.72fr)";
    return `minmax(${Math.min(18, 3.85 * pumpCount)}vw, ${Math.max(2.5, pumpCount * 0.72)}fr)`;
  }
  if (pumpCount <= 2) return "minmax(7.8vw, 0.55fr)";
  if (pumpCount <= 4) return "minmax(13.5vw, 1fr)";
  return `minmax(${Math.min(27, 5.83 * pumpCount)}vw, ${pumpCount}fr)`;
}

function getPumpAliasNumber(bomba) {
  const rawAlias = String(bomba?.alias ?? bomba?.bomba_id ?? "");
  const match = rawAlias.match(/\d+/);
  return match ? Number(match[0]) : null;
}

function CanRow({ can, moduleId, showCharts = true, generador, generadorApagado, splitByAliasParity = false, translate }) {
  const pumps = toArray(can?.bombas);
  const isCompactPair = pumps.length > 0 && pumps.length <= 2;
  const shouldSplitByAliasParity = splitByAliasParity && pumps.length > 0;
  const parityLineColumns = Math.max(1, Math.ceil(pumps.length / 2));
  const pumpItems = pumps.map((bomba, index) => ({ bomba, index, aliasNumber: getPumpAliasNumber(bomba) }));
  const sortedPumpItems = [...pumpItems].sort((a, b) => {
    const aliasA = a.aliasNumber ?? Number.MAX_SAFE_INTEGER;
    const aliasB = b.aliasNumber ?? Number.MAX_SAFE_INTEGER;
    return aliasA - aliasB || a.index - b.index;
  });
  const oddPumpItems = sortedPumpItems.filter((item) => item.aliasNumber == null || item.aliasNumber % 2 !== 0);
  const evenPumpItems = sortedPumpItems.filter((item) => item.aliasNumber != null && item.aliasNumber % 2 === 0);
  const cageRows = shouldSplitByAliasParity ? [oddPumpItems, evenPumpItems] : [pumpItems];
  const renderCage = ({ bomba, index }) => {
    const selectionKey = getPumpSelectionKey(bomba, {
      centerId: can?.centro_id,
      moduleId,
      canId: can?.can_id ?? can?.can,
      index,
    });

    return (
      <CageCard
        key={selectionKey}
        bomba={bomba}
        selectionKey={selectionKey}
        showCharts={showCharts}
        generador={generador}
        generadorApagado={generadorApagado}
        translate={translate}
      />
    );
  };

  return (
    <div
      className={`can-row ${isCompactPair ? "can-row--compact-pair" : ""} ${shouldSplitByAliasParity ? "can-row--parity-split" : ""} ${showCharts ? "" : "can-row--no-charts"}`}
      style={shouldSplitByAliasParity ? { "--can-row-line-columns": parityLineColumns } : undefined}
    >
      <div className="can-row__meta">
        <span>{nullable(safeFixed(can?.voltaje))} V</span>
        <span>{nullable(safeFixed(can?.corriente))} A</span>
        <span>{nullable(safeFixed(can?.presion))} bar</span>
        <span>{nullable(safeFixed(can?.sal))} ppt</span>
      </div>

      <div className="can-row__cages">
        {shouldSplitByAliasParity
          ? cageRows.map((rowItems, rowIndex) => (
              <div className="can-row__cageLine" key={rowIndex === 0 ? "odd" : "even"}>
                {rowItems.map(renderCage)}
              </div>
            ))
          : pumpItems.map(renderCage)}
      </div>
    </div>
  );
}

function CageCard({ bomba, selectionKey, showCharts = true, generador, generadorApagado, translate }) {
  const { activeMetric } = useContext(MetricCtx);
  const { selectedPumps, setSelectedPumps } = useContext(PumpCtx);
  const { animationPaused } = useScrollControl();
  const isSelected = selectedPumps.some((item) => getSelectedPumpKey(item) === selectionKey);
  const [alarmIndex, setAlarmIndex] = useState(0);

  const lm = bomba?.latest_measurements || {};
  const series = {
    TS: toArray(lm?.TS),
    O2: toArray(lm?.O2),
    SO2: toArray(lm?.SO2),
    Temp: toArray(lm?.Temp),
    Inyecting_O2: toArray(lm?.Inyecting_O2),
  };

  const n = Math.min(
    lengthOrInf(series.TS),
    lengthOrInf(series.O2),
    lengthOrInf(series.SO2),
    lengthOrInf(series.Temp)
  );

  const ts = series.TS.slice(-n);
  const o2 = series.O2.slice(-n);
  const so2 = series.SO2.slice(-n);
  const temp = series.Temp.slice(-n);
  const lastO2 = firstAvailable(lastOf(o2), bomba?.O2, bomba?.O2_bckp);
  const lastSO2 = firstAvailable(lastOf(so2), bomba?.SO2, bomba?.SO2_bckp);
  const lastTemp = firstAvailable(lastOf(temp), bomba?.Temp, bomba?.TEMP_bckp);
  const setPoint = bomba?.SetPoint;
  const setPointOFF = bomba?.SetPointOFF;

  let diffText = null;
  let diffColor = "var(--warn)";
  if (isNum(lastO2) && isNum(setPoint) && isNum(setPointOFF)) {
    if (lastO2 > setPoint) {
      diffText = `+${fmtNum(lastO2 - setPoint)}`;
      diffColor = "var(--ok)";
    } else if (lastO2 < setPointOFF) {
      diffText = `-${fmtNum(setPointOFF - lastO2)}`;
      diffColor = "var(--danger)";
    } else {
      diffText = t(translate, "dashboard_in_range", "En rango");
      diffColor = "var(--warn)";
    }
  }

  const metricsMap = {
    O2: { label: t(translate, "dashboard_dissolved_oxygen", "Oxigeno disuelto"), value: lastO2, display: fmtNum(lastO2), series: o2, unit: "mg/L", diffText, diffColor },
    SO2: { label: t(translate, "dashboard_oxygen_saturation", "Saturacion de oxigeno"), value: lastSO2, display: fmtNum(lastSO2), series: so2, unit: "%", diffText: null, diffColor: "var(--muted)" },
    Temp: { label: t(translate, "temperatura", "Temperatura"), value: lastTemp, display: fmtNum(lastTemp), series: temp, unit: "C", diffText: null, diffColor: "var(--muted)" },
  };

  const metricData = metricsMap[activeMetric] || metricsMap.O2;
  const metricSeries = metricData.series.filter((value) => isNum(Number(value))).map(Number);
  const hasSparkline = metricSeries.length > 1 && metricSeries.some((value) => value !== 0);
  const firstValue = metricSeries.length ? metricSeries[0] : null;
  const lastValue = metricSeries.length ? metricSeries[metricSeries.length - 1] : null;
  const trend = isNum(firstValue) && isNum(lastValue) ? lastValue - firstValue : null;
  const trendText = trend == null ? "-" : `${trend > 0 ? "+" : ""}${fmtNum(trend)}`;
  const trendColor = trend == null ? "var(--muted)" : trend >= 0 ? "var(--ok)" : "var(--danger)";
  const trendTooltipTitle = `${t(translate, "dashboard_variation_of", "Variacion de")} ${metricData.label}\n${t(translate, "dashboard_first_vs_last", "Primer valor vs ultimo valor del periodo")}`;
  const diffTooltipTitle = activeMetric === "O2"
    ? `${t(translate, "dashboard_threshold_difference_o2", "Diferencia contra umbral O2")}\n${t(translate, "min", "Min")} ${fmtNum(setPoint)}  / ${t(translate, "max", "max")} ${fmtNum(setPointOFF)}`
    : `${t(translate, "dashboard_threshold_difference", "Diferencia contra umbral")}\n${t(translate, "dashboard_not_apply_for", "No aplica para")} ${metricData.label}`;
  const voltageTooltipTitle = t(translate, "dashboard_cage_voltage", "Voltaje de la jaula");
  const currentTooltipTitle = t(translate, "dashboard_cage_current", "Corriente de la jaula");

  const modeAuto = Boolean(bomba?.InSel);
  const manualOn = Boolean(bomba?.InStart);
  const connected = Boolean(bomba?.Device_Modbus_Status);
  const sensorFault = bomba?.O2 === null || !bomba?.O2_Modbus_Status;
  const emergencyStop = Boolean(Number(bomba?.InStop));
  const lowPressure = Boolean(bomba?.aviot_status);
  const noO2Data = Number(bomba?.O2_bckp) === -1;
  const injectionSource = firstAvailable(lastOf(series.Inyecting_O2), bomba?.Inyecting_O2);
  const injecting = Boolean(Number(injectionSource));

  useEffect(() => {
    if (
      animationPaused ||
      !Array.isArray(bomba?.alarms) ||
      bomba.alarms.length <= 1
    ) {
      return;
    }
    const interval = setInterval(
      () => setAlarmIndex((previous) => (previous + 1) % bomba.alarms.length),
      4000
    );
    return () => clearInterval(interval);
  }, [animationPaused, bomba?.alarms]);

  const statusMessage = useMemo(
    () =>
      translateDashboardStatusMessage(getStatusMessage({
        controlType: "PS",
        bomba,
        bombaConectada: connected,
        paradaEmergencia: emergencyStop,
        alarmIndex,
        generador,
        generadorApagado,
      }), translate),
    [bomba, connected, emergencyStop, alarmIndex, generador, generadorApagado, translate]
  );
  const alertBadges = [
    statusMessage,
    lowPressure ? t(translate, "dashboard_low_pressure", "Presion baja") : null,
  ].filter(Boolean);
  const hasFailure = alertBadges.length > 0;
  const statusTone = hasFailure ? "danger" : noO2Data ? "offline" : !modeAuto ? "manual" : injecting ? "ok" : "idle";

  const toggleSelect = () => {
    setSelectedPumps((previous) =>
      isSelected
        ? previous.filter((item) => getSelectedPumpKey(item) !== selectionKey)
        : [...previous, { ...bomba, __selectionKey: selectionKey }]
    );
  };

  return (
    <button
      type="button"
      className={`cage-card cage-card--${statusTone} ${injecting ? "cage-card--injecting" : ""} ${alertBadges.length ? "cage-card--has-alert" : ""} ${hasSparkline && showCharts ? "" : "cage-card--no-spark"} ${showCharts ? "" : "cage-card--compact"} ${isSelected ? "is-selected" : ""}`}
      onClick={toggleSelect}
    >
      {injecting ? <BubblesOverlay /> : null}
      {alertBadges.length ? (
        <div className="cage-card__alerts" aria-label={alertBadges.join(", ")}>
          <WarningRoundedIcon className="cage-card__alertIcon" />
          <span>{alertBadges[0]}</span>
          {alertBadges.length > 1 ? <em>+{alertBadges.length - 1}</em> : null}
        </div>
      ) : null}
      <div className="cage-card__top">
        <span className="cage-card__name">
          <strong className="cage-card__id">{bomba?.alias || bomba?.bomba_id || "-"}</strong>
          {sensorFault ? <WarningRoundedIcon className="cage-card__sensorIcon" /> : null}
        </span>
        <span className="cage-card__value">
          {metricData.display}
          <small>{metricData.unit}</small>
        </span>
      </div>
      <div className="cage-card__statusIndicators">
        <div className="cage-card__mode">
          {modeAuto
            ? `TCB ${t(translate, "auto", "Auto")}`
            : `TCB ${t(translate, "manual", "Manual")} ${manualOn ? t(translate, "encendido", "Encendido") : t(translate, "apagado", "Apagado")}`}
        </div>
        <div className="cage-card__status">
          <span>{Boolean(bomba?.Control) ? t(translate, "auto", "Auto") : t(translate, "remoto", "Remoto")}</span>
        </div>
      </div>
      {hasSparkline && showCharts ? (
        <div className="cage-card__spark">
          <MetricChart
            label={activeMetric}
            series={metricData.series}
            ts={ts}
            strokeColor="var(--cyan)"
            setPoint={setPoint}
            setPointOFF={setPointOFF}
            Inyecting_O2={series.Inyecting_O2}
            pad={2}
          />
        </div>
      ) : null}
      <div className="cage-card__footer">
        <CustomTooltip variant="pipeline" color="neutral" title={<span className="cage-card__metricTooltip">{trendTooltipTitle}</span>}>
          <span className="cage-card__footerMetric" style={{ color: trendColor }}>{trendText}</span>
        </CustomTooltip>
        <CustomTooltip variant="pipeline" color="neutral" title={<span className="cage-card__metricTooltip">{diffTooltipTitle}</span>}>
          <span className="cage-card__footerMetric" style={{ color: metricData.diffColor }}>{metricData.diffText || "-"}</span>
        </CustomTooltip>
        <CustomTooltip variant="pipeline" color="neutral" title={<span className="cage-card__metricTooltip">{voltageTooltipTitle}</span>}>
          <span className="cage-card__footerMetric" style={{ color: '#c3c3c3' }}>{isNum(bomba?.Vo) ? `${Number(bomba.Vo).toFixed(2)}V` : "-V"}</span>
        </CustomTooltip>
        <CustomTooltip variant="pipeline" color="neutral" title={<span className="cage-card__metricTooltip">{currentTooltipTitle}</span>}>
          <span className="cage-card__footerMetric" style={{ color: '#c3c3c3' }}>{isNum(bomba?.Io) ? `${Number(bomba.Io).toFixed(2)}A` : "-A"}</span>
        </CustomTooltip>
      </div>
    </button>
  );
}

function DashboardCenterSkeleton() {
  return (
    <div className="center-row itgDash__colCentro itgDash__colCentro--skeleton" aria-hidden="true">
      <div className="dashboard-skeleton__summary">
        <Skeleton variant="text" width="34%" height={18} />
        <Skeleton variant="rounded" width={120} height={10} />
        <div className="dashboard-skeleton__actions">
          <Skeleton variant="circular" width={18} height={18} />
          <Skeleton variant="circular" width={18} height={18} />
        </div>
      </div>

      <div className="dashboard-skeleton__modules">
        <section className="dashboard-skeleton__module">
          <div className="dashboard-skeleton__moduleHead">
            <Skeleton variant="text" width={92} height={18} />
            <Skeleton variant="text" width="22%" height={14} />
          </div>
          <div className="dashboard-skeleton__canMeta">
            <Skeleton variant="text" width="18%" height={14} />
            <Skeleton variant="text" width="14%" height={14} />
          </div>
          <div className="dashboard-skeleton__cages">
            {Array.from({ length: 4 }).map((_, cageIndex) => (
              <Skeleton
                key={`cage-skeleton-${cageIndex}`}
                variant="rounded"
                width="100%"
                height="6.4vh"
              />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function AutoScrollContainer({ children }) {
  const containerRef = useRef(null);
  const { animationPaused, globalSpeed } = useScrollControl(); // globalSpeed ahora = velocidad en px/s
  const [shouldScroll, setShouldScroll] = useState(false);
  const [duration, setDuration] = useState(15); // duración calculada dinámica
  const [dragging, setDragging] = useState(false);
  const startY = useRef(0);
  const scrollTopStart = useRef(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const contentHeight = el.scrollHeight;
    const visibleHeight = el.clientHeight;

    const needsScroll = contentHeight > visibleHeight + 20;
    setShouldScroll(needsScroll);

    if (needsScroll) {
      // 🔹 Calcular duración para una velocidad constante (px/s)
      const pxPerSecond = globalSpeed || 15; // velocidad global (px/s)
      const distance = contentHeight / 2; // porque se anima -50%
      const seconds = distance / pxPerSecond;
      setDuration(seconds);
    }
  }, [children, globalSpeed]);

  // 🔹 Scroll manual
  const handleWheel = (e) => {
    if (animationPaused) {
      containerRef.current.scrollTop += e.deltaY * 0.5;
    }
  };

  // 🔹 Drag
  const handleMouseDown = (e) => {
    if (!animationPaused) return;
    setDragging(true);
    startY.current = e.clientY;
    scrollTopStart.current = containerRef.current.scrollTop;
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    const delta = e.clientY - startY.current;
    containerRef.current.scrollTop = scrollTopStart.current - delta;
  };

  const handleMouseUp = () => setDragging(false);

  return (
    <div
      ref={containerRef}
      className={`scroll-animate ${animationPaused ? "scroll-paused" : ""}`}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {shouldScroll ? (
        <div
          className={`scroll-inner ${animationPaused ? "scroll-paused" : ""}`}
          style={{
            animationDuration: `${duration}s`,
            cursor: animationPaused ? (dragging ? "grabbing" : "grab") : "default",
          }}
        >
          {children}
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  );
}

/* ---------- Burbuja de promed0ios por módulo ---------- */

function getO2StatusColor(value, minThreshold, maxThreshold) {
  const avg = Number(value);
  const min = Number(minThreshold);
  const max = Number(maxThreshold);

  if (!Number.isFinite(avg) || !Number.isFinite(min) || !Number.isFinite(max)) {
    return "#c3c3c3";
  }

  if (avg < min) {
    return "rgb(240, 42, 42)"; // rojo
  }

  if (avg <= max) {
    return "rgb(168, 131, 38)"; // amarillo
  }

  return "rgb(41, 111, 77)"; // verde
}

function CentersStats({ centersData, translate }) {
  const labelsRevision = useLabelVersion();
  const { hoverTS } = useContext(HoverCtx);
  const { activeMetric } = useContext(MetricCtx);

  const viewportRef = useRef(null);
  const contentRef = useRef(null);
  const [shouldMarquee, setShouldMarquee] = useState(false);

  const centersAverages = useMemo(() => {
    return centersData
      .filter((center) => {
        const fullId =
          center?.centro || center?.modulos?.[0]?.cans?.[0]?.centro_id;

        if (!fullId) return false;

        const name = fullId.split("-").pop().trim();
        return name && name !== "—";
      })
      .map((center) => {
        const mods = toArray(center?.modulos);
        const averages = computeModuleAverages(mods, hoverTS);

        const key =
          activeMetric === "O2"
            ? "o2"
            : activeMetric === "SO2"
            ? "so2"
            : "temp";

        const vals = averages.map((m) => m[key]).filter(isNum);
        const avg = vals.length
          ? vals.reduce((a, b) => a + b, 0) / vals.length
          : null;

        const totals = countTotals(mods);
        const minThreshold =
          center?.umbrales?.setpoin_on || center?.umbrales?.[0]?.Min || "—";
        const maxThreshold =
          center?.umbrales?.setpoin_off || center?.umbrales?.[0]?.Max || "—";

        const fullId =
          center?.centro || center?.modulos?.[0]?.cans?.[0]?.centro_id;
        const name = fullId ? fullId.split("-").pop().trim() : "Centro";

        return {
          name,
          label: labelId(fullId, "rac", name),
          avg,
          totals,
          lastMeasurement: center?.hora || "—",
          minThreshold,
          maxThreshold,
        };
      });
  }, [centersData, hoverTS, activeMetric, labelsRevision]); // eslint-disable-line react-hooks/exhaustive-deps -- La versión invalida labels del resolvedor en memoria.

  useLayoutEffect(() => {
    const checkOverflow = () => {
      const viewport = viewportRef.current;
      const content = contentRef.current;

      if (!viewport || !content) return;

      setShouldMarquee(content.scrollWidth > viewport.clientWidth);
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    if (viewportRef.current) resizeObserver.observe(viewportRef.current);
    if (contentRef.current) resizeObserver.observe(contentRef.current);

    window.addEventListener("resize", checkOverflow);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [centersAverages]);

  const renderItems = (items, duplicated = false) =>
    items.map((c, i) => {
      const valueColor = getO2StatusColor(c.avg, c.minThreshold, c.maxThreshold);
      
      return(
      <div
        key={`${duplicated ? "dup" : "orig"}-${c.name}-${i}`}
        className="centersStats__item"
      >
        <h4 className="centersStats__title">
          {c.label ?? c.name}
          <small
            className="centersStats__avg"
            style={{
              color:
                activeMetric === "O2" || "SO2"
                  ? getO2StatusColor(c.avg, c.minThreshold, c.maxThreshold)
                  : "#c3c3c3",
            }}
          >
            {c.avg != null ? fmtNum(c.avg) : "—"} {activeMetric === "O2" ? 'mg/L': (activeMetric === "SO2" ? '%': '°C') }
          </small>
          {/* <small className="centersStats__avg">
            {c.avg != null ? fmtNum(c.avg) : "—"}
          </small> */}
        </h4>

        {/* <div className="centersStats__metaRow">
          <span className="centersStats__metaLabel">
            {t(translate, "ultima_medicion", "Última medición")}
          </span>
          <span className="centersStats__metaValue">
            {formatCenterStatsMeasurement(c.lastMeasurement)}
          </span>
          <span className="centersStats__metaLabel">
            {t(translate, "umbral", "Umbral O2")}
          </span>
          <span className="centersStats__metaValue">
            min: {c.minThreshold} / max:{c.maxThreshold}
          </span>
        </div> */}
      </div>
    )
  });

  return (
    <div className="centersStatsViewport" ref={viewportRef}>
      <div
        className={`centersStatsTrack ${shouldMarquee ? "is-marquee" : ""}`}
      >
        <div className="centersStatsGroup" ref={contentRef}>
          {renderItems(centersAverages)}
        </div>

        {shouldMarquee && (
          <div className="centersStatsGroup centersStatsGroup--clone" aria-hidden="true">
            {renderItems(centersAverages, true)}
          </div>
        )}
      </div>
    </div>
  );
}


/* ---------- Subcomponentes ---------- */

function ModuleCard({ modulo, generador, generadorApagado, translate }) {
  // console.log('generador',generador)
  const [collapsed, setCollapsed] = useState(false);
  const modName = modulo?.cans?.[0]?.modulo || modulo?.id || t(translate, "dashboard_unnamed_module", "Modulo sin nombre");
  
  return (
    <section className="itgDash__module">
      <div className="itgDash__moduleHead">
        <h2 style={{fontSize:'min(1vw, 2.4vh)'}}>{t(translate, "modulo", "Modulo")} {String(modName)}</h2>
        {/* <small className="muted">{safeText(modulo?.cans?.[0]?.centro_id)}</small> */}
        <button
          className="collapseBtn"
          onClick={() => setCollapsed((c) => !c)}
        >
          {collapsed ? "➕" : "➖"}
        </button>
      </div>
      {!collapsed && (
        <div className="itgDash__moduleBody" >
          {(modulo?.cans || []).map((can, idx) => (
            <CanCard
              key={can?.can_id ?? idx}
              can={can}
              moduleId={modulo?.id ?? modName}
              generador={generador}
              generadorApagado={generadorApagado}
              translate={translate}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function safeFixed(value, decimals = 2) {
  return value != null && !isNaN(value) ? value.toFixed(decimals) : null;
}

function CanCard({ can, moduleId, generador, generadorApagado, translate }) {
  const { showCharts } = useChartsVisibility();
  const [collapsed, setCollapsed] = useState(false);
  const canLabel = can?.can || can?.can_id || "CAN";
  const badge = can?.estado ? (
    <span className="badge badge--ok">ON</span>
  ) : (
    <span className="badge badge--off">OFF</span>
  );
  return (
    <div className="itgDash__can card">
      <div className="itgDash__canHead">
        <div className="itgDash__canMeta">
          {/* {badge} */}
          <small className="muted">{nullable(safeFixed(can?.voltaje))} V</small>
          <small className="muted">{nullable(safeFixed(can?.corriente))} A</small>
          <small className="muted">{nullable(safeFixed(can?.presion))} bar</small>
          <small className="muted">{nullable(safeFixed(can?.sal))} ppt</small>
          {/* <small className="muted">{formatAbs(can?.timestamp) ?? "—"}</small> */}
          <button
            className="collapseBtn"
            onClick={() => setCollapsed((c) => !c)}
          >
            {collapsed ? "➕" : "➖"}
          </button>
        </div>
      </div>
      {!collapsed && (
        <div 
        // className="itgDash__pumps"
        className={`itgDash__pumps ${!showCharts ? "pumps--compact" : ""}`}
        >
          {(can?.bombas || []).map((b, i) => {
            const selectionKey = getPumpSelectionKey(b, {
              centerId: can?.centro_id,
              moduleId,
              canId: can?.can_id ?? can?.can,
              index: i,
            });

            return showCharts ? (
              <PumpCardChart
                key={selectionKey}
                bomba={b}
                selectionKey={selectionKey}
                generador={generador}
                generadorApagado={generadorApagado}
                translate={translate}
              />
            ) : (
              <PumpCard
                key={selectionKey}
                bomba={b}
                selectionKey={selectionKey}
                generador={generador}
                generadorApagado={generadorApagado}
                translate={translate}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function PumpCard({ bomba, selectionKey, controlType = "PS", generador, generadorApagado, translate }) {
  const { activeMetric } = useContext(MetricCtx);
  const { showCharts } = useChartsVisibility();
  const { selectedPumps, setSelectedPumps } = useContext(PumpCtx);
  const { animationPaused } = useScrollControl();
  const isSelected = selectedPumps.some((b) => getSelectedPumpKey(b) === selectionKey);

  const [hovered, setHovered] = useState(false); // 👈 estado hover

  const fallaSensor = bomba.O2 === null || !bomba.O2_Modbus_Status;
  const ManualAuto = Boolean(bomba.InStart);
  const bombaConectada = bomba.Device_Modbus_Status;
  const modeAuto = Boolean(bomba.InSel);
  const isInjecting = Boolean(bomba?.Inyecting_O2);
  const paradaEmergencia = Boolean(Number(bomba.InStop));

  const lm = bomba?.latest_measurements || {};
  const series = {
    TS: toArray(lm?.TS),
    O2: toArray(lm?.O2),
    SO2: toArray(lm?.SO2),
    Temp: toArray(lm?.Temp),
    Inyecting_O2: toArray(lm?.Inyecting_O2),
  };

  const n = Math.min(
    lengthOrInf(series.TS),
    lengthOrInf(series.O2),
    lengthOrInf(series.SO2),
    lengthOrInf(series.Temp)
  );

  const ts = series.TS.slice(-n);
  const o2 = series.O2.slice(-n);
  const so2 = series.SO2.slice(-n);
  const temp = series.Temp.slice(-n);

  const lastO2 = firstAvailable(lastOf(o2), bomba?.O2, bomba?.O2_bckp);
  const lastSO2 = firstAvailable(lastOf(so2), bomba?.SO2, bomba?.SO2_bckp);
  const lastTemp = firstAvailable(lastOf(temp), bomba?.Temp, bomba?.TEMP_bckp);

  const setPoint = bomba?.SetPoint;
  const setPointOFF = bomba?.SetPointOFF;

  //  Diferencia y color según rango
  let diffText = null;
  let diffColor = "var(--warn)";
  if (isNum(lastO2) && isNum(setPoint) && isNum(setPointOFF)) {
    if (lastO2 > setPoint) {
      diffText = `+${fmtNum(lastO2 - setPoint)}`;
      diffColor = "var(--ok)";
    } else if (lastO2 < setPointOFF) {
      diffText = `-${fmtNum(setPointOFF - lastO2)}`;
      diffColor = "var(--danger)";
    } else {
      diffText = t(translate, "dashboard_in_range", "En rango");
      diffColor = "var(--warn)";
    }
  }

  const metricsMap = {
    O2: {
      label: "O₂",
      value: fmtNum(lastO2),
      series: o2,
      strokeColor: "var(--cyan)",
      unit: "mg/L",
      extraDiff: diffText,
      extraColor: diffColor,
    },
    SO2: {
      label: "SO₂",
      value: fmtNum(lastSO2),
      series: so2,
      strokeColor: "var(--cyan)",
      unit: "%",
    },
    Temp: {
      label: "Temp",
      value: fmtNum(lastTemp),
      series: temp,
      strokeColor: "#7f1d1d",
      unit: "°C",
    },
  };

  const metricData = metricsMap[activeMetric];

  const toggleSelect = () => {
    setSelectedPumps((prev) =>
      isSelected
        ? prev.filter((b) => getSelectedPumpKey(b) !== selectionKey)
        : [...prev, { ...bomba, __selectionKey: selectionKey }]
    );
  };

  const [alarmIndex, setAlarmIndex] = useState(0);

  // === Rotación de alarmas ===
  useEffect(() => {
    if (
      animationPaused ||
      controlType !== "PS" ||
      !Array.isArray(bomba.alarms) ||
      bomba.alarms.length <= 0
    ) {
      return;
    }
    const interval = setInterval(
      () => setAlarmIndex((prev) => (prev + 1) % bomba.alarms.length),
      4000
    );
    return () => clearInterval(interval);
  }, [animationPaused, controlType, bomba.alarms]);

  // === Mensaje de estado ===
  const statusMessage = useMemo(
    () =>
      translateDashboardStatusMessage(getStatusMessage({
        controlType,
        bomba,
        bombaConectada,
        paradaEmergencia,
        alarmIndex,
        generador,
        generadorApagado
      }), translate),
    [controlType, bomba, bombaConectada, paradaEmergencia, alarmIndex, generador, generadorApagado, translate]
  );

  const pipelineBg =
    bomba.O2_bckp === -1
      ? "linear-gradient(to left, rgba(39, 39, 39, 1), rgba(68, 68, 68, 1))"
      : !modeAuto
      ? "linear-gradient(to left, rgb(34, 34, 34), rgb(68, 66, 71))"
      : "linear-gradient(to left, rgb(20, 30, 48), rgb(36, 59, 85))";

  const alertColor = "#b91c1c";

  return (
    <div
      className={`itgDash__pump card`}
      // className={`itgDash__pump card ${!showCharts ? "pumps--compact" : ""}`}
      style={{
        background: pipelineBg,
        position: "relative",
        transition: "all 0.2s ease",
      }}
      onClick={toggleSelect}
      onMouseEnter={() => setHovered(true)} // 👈 hover ON
      onMouseLeave={() => setHovered(false)} // 👈 hover OFF
    >
      <div className="itgDash__pumpContent">
        <div className="itgDash__leftCol">
          <div className="itgDash__topRow">
            <div className="itgDash__pumpHead">
              <h4 style={{ fontSize: "max(0.7vw, 1vh)" }}>{bomba?.alias || t(translate, "go3d_metric_pump", "Bomba")}</h4>
              {fallaSensor && (
                <WarningRoundedIcon
                  style={{
                    fontSize: "min(0.8vw, 1.6vh)",
                    color: "#a88326",
                    position: "absolute",
                    left: "1.4vw",
                    top: "0vh",
                    zIndex:10
                  }}
                />
              )}
              <h4 className="muted">
                {Boolean(bomba.Control) ? t(translate, "auto", "Auto") : t(translate, "remoto", "Remoto") || t(translate, "control", "Control")}
              </h4>
            </div>

            {isInjecting && <BubblesOverlay />}
          </div>

          <div className="pumpFooter muted"  >
            <p className="caption-text" style={{lineHeight:'1.2vh'}} >
              {modeAuto
                ? `TCB ${t(translate, "auto", "Auto")}`
                : `TCB ${t(translate, "manual", "Manual")} ${ManualAuto ? t(translate, "encendido", "Encendido") : t(translate, "apagado", "Apagado")}`}
            </p>
            <p className="caption-text" style={{lineHeight:'1vh'}}>
              {!bombaConectada
                ? "0 V | 0 A"
                : `${bomba.Vo ?? "--"} V | ${bomba.Io ?? "--"} A`}
            </p>
          </div>
        </div>
        <div style={{position:'absolute', marginLeft:'2.2vw', marginTop:'-2vh' }}>
          <Metric
            label={metricData.label}
            value={metricData.value}
            unit={metricData.unit}
            series={metricData.series}
            ts={ts}
            extraDiff={metricData.extraDiff}
            extraColor={metricData.extraColor}
            setPoint={setPoint}
            setPointOFF={setPointOFF}
          />
        </div>
      </div>
    </div>
  );
}

function PumpCardChart({ bomba, selectionKey, controlType = "PS", generador, generadorApagado, translate }) {
  const { activeMetric } = useContext(MetricCtx);
  const { showCharts } = useChartsVisibility();
  const { selectedPumps, setSelectedPumps } = useContext(PumpCtx);
  const { animationPaused } = useScrollControl();
  const isSelected = selectedPumps.some((b) => getSelectedPumpKey(b) === selectionKey);

  const [hovered, setHovered] = useState(false); // 👈 estado hover

  const fallaSensor = bomba.O2 === null || !bomba.O2_Modbus_Status;
  const ManualAuto = Boolean(bomba.InStart);
  const bombaConectada = bomba.Device_Modbus_Status;
  const modeAuto = Boolean(bomba.InSel);
  const isInjecting = Boolean(bomba?.Inyecting_O2);
  const paradaEmergencia = Boolean(Number(bomba.InStop));

  const lm = bomba?.latest_measurements || {};
  const series = {
    TS: toArray(lm?.TS),
    O2: toArray(lm?.O2),
    SO2: toArray(lm?.SO2),
    Temp: toArray(lm?.Temp),
    Inyecting_O2: toArray(lm?.Inyecting_O2),
  };

  const n = Math.min(
    lengthOrInf(series.TS),
    lengthOrInf(series.O2),
    lengthOrInf(series.SO2),
    lengthOrInf(series.Temp)
  );

  const ts = series.TS.slice(-n);
  const o2 = series.O2.slice(-n);
  const so2 = series.SO2.slice(-n);
  const temp = series.Temp.slice(-n);

  const lastO2 = firstAvailable(lastOf(o2), bomba?.O2, bomba?.O2_bckp);
  const lastSO2 = firstAvailable(lastOf(so2), bomba?.SO2, bomba?.SO2_bckp);
  const lastTemp = firstAvailable(lastOf(temp), bomba?.Temp, bomba?.TEMP_bckp);

  const setPoint = bomba?.SetPoint;
  const setPointOFF = bomba?.SetPointOFF;

  // Diferencia y color según rango
  let diffText = null;
  let diffColor = "var(--warn)";
  if (isNum(lastO2) && isNum(setPoint) && isNum(setPointOFF)) {
    if (lastO2 > setPoint) {
      diffText = `+${fmtNum(lastO2 - setPoint)}`;
      diffColor = "var(--ok)";
    } else if (lastO2 < setPointOFF) {
      diffText = `-${fmtNum(setPointOFF - lastO2)}`;
      diffColor = "var(--danger)";
    } else {
      diffText = t(translate, "dashboard_in_range", "En rango");
      diffColor = "var(--warn)";
    }
  }

  const metricsMap = {
    O2: {
      label: "O₂",
      value: fmtNum(lastO2),
      series: o2,
      strokeColor: "var(--cyan)",
      unit: "mg/L",
      extraDiff: diffText,
      extraColor: diffColor,
    },
    SO2: {
      label: "SO₂",
      value: fmtNum(lastSO2),
      series: so2,
      strokeColor: "var(--cyan)",
      unit: "%",
    },
    Temp: {
      label: "Temp",
      value: fmtNum(lastTemp),
      series: temp,
      strokeColor: "#7f1d1d",
      unit: "°C",
    },
  };

  const metricData = metricsMap[activeMetric];

  const toggleSelect = () => {
    setSelectedPumps((prev) =>
      isSelected
        ? prev.filter((b) => getSelectedPumpKey(b) !== selectionKey)
        : [...prev, { ...bomba, __selectionKey: selectionKey }]
    );
  };

  const [alarmIndex, setAlarmIndex] = useState(0);

  // === Rotación de alarmas ===
  useEffect(() => {
    if (
      animationPaused ||
      controlType !== "PS" ||
      !Array.isArray(bomba.alarms) ||
      bomba.alarms.length <= 0
    ) {
      return;
    }
    const interval = setInterval(
      () => setAlarmIndex((prev) => (prev + 1) % bomba.alarms.length),
      4000
    );
    return () => clearInterval(interval);
  }, [animationPaused, controlType, bomba.alarms]);

  // === Mensaje de estado ===
  const statusMessage = useMemo(
    () =>
      translateDashboardStatusMessage(getStatusMessage({
        controlType,
        bomba,
        bombaConectada,
        paradaEmergencia,
        alarmIndex,
        generador,
        generadorApagado
      }), translate),
    [controlType, bomba, bombaConectada, paradaEmergencia, alarmIndex, generador, generadorApagado, translate]
  );

  const pipelineBg =
    bomba.O2_bckp === -1
      ? "linear-gradient(to left, rgba(39, 39, 39, 1), rgba(68, 68, 68, 1))"
      : !modeAuto
      ? "linear-gradient(to left, rgb(34, 34, 34), rgb(68, 66, 71))"
      : "linear-gradient(to left, rgb(20, 30, 48), rgb(36, 59, 85))";

  const alertColor = "#b91c1c";

  return (
    <div
      className={`itgDash__pump_chart card`}
      style={{
        background: pipelineBg,
        position: "relative",
        transition: "all 0.2s ease",
      }}
      onClick={toggleSelect}
      onMouseEnter={() => setHovered(true)} // 👈 hover ON
      onMouseLeave={() => setHovered(false)} // 👈 hover OFF
    >
      {/*  Alerta visual con opacidad controlada */}
      {statusMessage && (
        <div
          className="pumpAlert"
          style={{
            backgroundColor: "#4d1e1eff",
            border: `0.01vw solid ${alertColor}`,
            borderRadius: "min(0.4vw, 0.8vh)",
            padding: "0.3vh 0.4vw",
            display: "flex",
            alignItems: "center",
            gap: "0.4vw",
            marginTop: "1vh",
            fontSize: "min(0.6vw, 1.2vh)",
            color: alertColor,
            opacity: hovered ? 0.4 : 1, // aquí se atenúa
            zIndex: hovered ? 0 : 8,
            transition: "opacity 0.25s ease",
          }}
        >
          <WarningRoundedIcon style={{ fontSize: "min(0.8vw, 1.6vh)", color: alertColor }} />
          <span>{statusMessage}</span>
        </div>
      )}

      <div className="itgDash__pumpContent_chart">
        <div className="itgDash__leftCol_chart">
          <div className="itgDash__topRow_chart">
            <div className="itgDash__pumpHead_chart">
              <h4 style={{ fontSize: "min(0.7vw, 1.4vh)" }}>{bomba?.alias || t(translate, "go3d_metric_pump", "Bomba")}</h4>
              {fallaSensor && (
                <WarningRoundedIcon
                  style={{
                    fontSize: "min(0.8vw, 1.6vh)",
                    color: "#a88326",
                    position: "absolute",
                    left: "1.4vw",
                    top: "0vh",
                  }}
                />
              )}
              <h4 className="muted">
                {Boolean(bomba.Control) ? t(translate, "auto", "Auto") : t(translate, "remoto", "Remoto") || t(translate, "control", "Control")}
              </h4>
            </div>

            {isInjecting && <BubblesOverlay />}
            <MetricChart
              label={metricData.label}
              series={metricData.series}
              ts={ts}
              strokeColor={metricData.strokeColor}
              setPoint={setPoint}
              setPointOFF={setPointOFF}
              Inyecting_O2={toArray(lm?.Inyecting_O2)}
            />
          </div>

          <div className="pumpFooter_chart">
            <p className="caption-text" style={{lineHeight: modeAuto ? '1.2vh' : '1.1vh'}} >
              {modeAuto
                ? `TCB ${t(translate, "auto", "Auto")}`
                : `TCB ${t(translate, "manual", "Manual")} ${ManualAuto ? t(translate, "encendido", "Encendido") : t(translate, "apagado", "Apagado")}`}
            </p>
            <p className="caption-text">
              {!bombaConectada
                ? "0 V | 0 A"
                : `${bomba.Vo ?? "--"} V | ${bomba.Io ?? "--"} A`}
            </p>
          </div>
        </div>

        <Metric
          label={metricData.label}
          value={metricData.value}
          unit={metricData.unit}
          series={metricData.series}
          ts={ts}
          extraDiff={metricData.extraDiff}
          extraColor={metricData.extraColor}
          setPoint={setPoint}
          setPointOFF={setPointOFF}
        />
      </div>
    </div>
  );
}


function BubblesOverlay({ isActive = true }) {
  const { animationPaused } = useScrollControl();
  // 🔹 Más burbujas visibles en pantalla
  const NUM_BURBUJAS = 50;

  const burbujas = useMemo(
    () =>
      Array.from({ length: NUM_BURBUJAS }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,        // posición horizontal
        size: 4 + Math.random() * 8,      // tamaño relativo de las burbujas
        duration: 10 + Math.random() * 6, // 🔹 más lenta: 10–16 segundos
        delay: Math.random() * 4,         // 🔹 menos delay = aparecen más seguido
        opacity: 0.4 + Math.random() * 0.6,
        drift: Math.random() * 4 - 2,     // leve desplazamiento horizontal
      })),
    []
  );

  if (!isActive) return null;

  return (
    <div className="bubbles-overlay-dash">
      {burbujas.map((b) => (
        <div
          key={b.id}
          className="bubble-dash"
          style={{
            left: `${b.left}%`,
            width: `${b.size / 20}vw`,
            height: `${b.size / 20}vw`,
            opacity: b.opacity,
            animation: `bubble-rise ${b.duration}s linear ${b.delay}s infinite`,
            animationPlayState: animationPaused ? "paused" : "running",
            "--drift": `${b.drift}%`,
          }}
        />
      ))}
    </div>
  );
}





function PumpDetail({ bombas, activeMetric = "O2" }) {
  const chartRef = React.useRef();
  const { hoverTS } = React.useContext(HoverCtx);
  React.useEffect(() => {
    if (!hoverTS || !chartRef.current) return;
    const chart = chartRef.current;

    // buscar índice más cercano al TS
    chart.data.datasets.forEach((ds, datasetIndex) => {
      const idx = ds.data.findIndex(p => new Date(p.x).getTime() === new Date(hoverTS).getTime());
      if (idx >= 0) {
        chart.setActiveElements([{ datasetIndex, index: idx }]);
      }
    });

    chart.update();
  }, [hoverTS]);

  const datasets = bombas.map((bomba, i) => {
    const lm = bomba?.latest_measurements || {};
    const ts = Array.isArray(lm?.TS) ? lm.TS : [];
    const vals = Array.isArray(lm?.[activeMetric]) ? lm[activeMetric] : [];

    const dataPoints = ts.map((t, idx) => ({
      x: new Date(t),
      y: Number(vals[idx]) || null,
    })).filter(p => p.y !== null && !isNaN(p.y));

    return {
      label: bomba.alias || `Bomba ${i + 1}`,
      data: dataPoints,
      borderColor: `hsl(${(i * 60) % 360}, 70%, 50%)`,
      borderWidth: 2,
      tension: 0.3,
      pointRadius: 0,
    };
  });

  const data = { datasets };

  // Calcular yMin / yMax dinámicos
  let yMin = null, yMax = null;
  if (datasets.length > 0) {
    const bomba = bombas[0]; // 👈 si quieres calcular sobre la primera (o podrías mergear todas)
    const allData = datasets.flatMap(ds => ds.data.map(d => d.y));

    if (activeMetric === "O2") {
      yMin = bomba.SetPoint ?? 0;
      yMax = bomba.SetPointOFF ?? 10;

      const vals = allData.filter(v => v !== undefined && v !== null && v !== -1 && v !== -100);
      if (vals.length) {
        const minData = Math.min(...vals);
        const maxData = Math.max(...vals);
        if (minData < yMin) yMin = minData;
        if (maxData > yMax) yMax = maxData;
      }
    } else if (activeMetric === "SO2") {
      yMin = (bomba.SetPoint ?? 0) * 10;
      yMax = (bomba.SetPointOFF ?? 10) * 10;

      const vals = allData.filter(v => v !== undefined && v !== null && v !== -100);
      if (vals.length) {
        const minData = Math.min(...vals);
        const maxData = Math.max(...vals);
        if (minData < yMin) yMin = minData;
        if (maxData > yMax) yMax = maxData;
      }
    } else {
      const vals = allData.filter(v => v !== undefined && v !== null);
      if (vals.length) {
        const minData = Math.min(...vals);
        const maxData = Math.max(...vals);
        yMin = minData - Math.abs(minData) * 0.2;
        yMax = maxData + Math.abs(maxData) * 0.2;
      }
    }
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "time",
        time: { unit: "minute" },
        title: { display: true, text: "Tiempo" },
      },
      y: {
        title: { display: true, text: activeMetric },
        suggestedMin: yMin,
        suggestedMax: yMax,
      },
    },
    plugins: {
      legend: { position: "bottom" },
      tooltip: { enabled: true },
    },
  };

  return (
    <div style={{ width: "100%", height: "50vh" }}>
      <h3>Detalle de {bombas.length} bomba(s)</h3>
      <Line ref={chartRef} data={data} options={options} />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="stat">
      <div className="stat__value">{value}</div>
      <div className="stat__label">{label}</div>
    </div>
  );
}

/* ---------- Métrica con sparkline + sincronización global ---------- */

function MetricChart({
  label,
  series = [],
  ts = [],
  strokeColor,
  setPoint,
  setPointOFF, // 👈 llegan aquí
  Inyecting_O2,
  pad = 0,
}) {
  const { hoverTS, active } = useContext(HoverCtx);

  const localIdx = useMemo(() => {
    if (!hoverTS || !Array.isArray(ts) || ts.length === 0) return null;
    return findNearestIdx(ts, hoverTS);
  }, [hoverTS, ts]);

  const min = isNumArr(series) ? Math.min(...series) : 0;
  const max = isNumArr(series) ? Math.max(...series) : 1;

  // valor puntual al hacer hover
  const atVal = localIdx != null ? series?.[localIdx] : null;
  const tipVal = isNum(atVal) ? Number(atVal) : null;
  const sp = Number(setPoint);
  const spOff = Number(setPointOFF);

  let tipDiff = null;
  let tipColor = "var(--warn)";
  if (isNum(tipVal) && isNum(sp) && isNum(spOff)) {
    if (tipVal > sp) {
      tipDiff = `+${fmtNum(tipVal - sp)}`;
      tipColor = "var(--ok)";
    } else if (tipVal < spOff) {
      tipDiff = `-${fmtNum(spOff - tipVal)}`;
      tipColor = "var(--danger)";
    } else {
      tipDiff = `En rango`;
      tipColor = "var(--warn)";
    }
  }

  return (
    <>
        <div className="metric__chart">
          <Sparkline
            values={series}
            ts={ts}
            min={min}
            max={max}
            sharedIdx={localIdx}
            strokeColor={strokeColor}
            Inyecting_O2={Inyecting_O2}
            pad={pad}
          />
        </div>
      {/* Tooltip local */}
      {active && localIdx != null && (
        <div className="metric__tooltip">
          <div className="metric__tooltipLine">
            <span className="metric__tooltipKey">{label} :   </span>
            <span className="metric__tooltipVal">
              {tipVal != null ? fmtNum(tipVal) : "—"}
              {/* {unit ? ` ${unit}` : ""} */}
            </span>
          </div>
          {tipDiff && (
            <div className="metric__tooltipDiff" style={{ color: tipColor }}>
              {tipDiff}
            </div>
          )}
        </div>
      )}
    </>
  );
}

function Metric({
  label,
  value,
  unit,
  series = [],
  ts = [],
  extraDiff,
  extraColor,
  setPoint,
  setPointOFF,
}) {
  const { hoverTS, active } = useContext(HoverCtx);
  const { showCharts } = useChartsVisibility();

  const localIdx = useMemo(() => {
    if (!hoverTS || !Array.isArray(ts) || ts.length === 0) return null;
    return findNearestIdx(ts, hoverTS);
  }, [hoverTS, ts]);

  // === Diferencia respecto al SetPoint (Diff) ===
  const atVal = localIdx != null ? series?.[localIdx] : null;
  const tipVal = isNum(atVal) ? Number(atVal) : null;
  const sp = Number(setPoint);
  const spOff = Number(setPointOFF);

  let tipDiff = null;
  let tipColor = "var(--warn)";
  let DiffIcon = TrendingFlatIcon;

  if (isNum(tipVal) && isNum(sp) && isNum(spOff)) {
    if (tipVal > sp) {
      tipDiff = `+${fmtNum(tipVal - sp)}`;
      tipColor = "var(--ok)";
      DiffIcon = ArrowUpwardRoundedIcon;
    } else if (tipVal < spOff) {
      tipDiff = `-${fmtNum(spOff - tipVal)}`;
      tipColor = "var(--danger)";
      DiffIcon = ArrowDownwardRoundedIcon;
    } else {
      tipDiff = `En rango`;
      tipColor = "var(--warn)";
      DiffIcon = TrendingFlatIcon;
    }
  }

  // === Tendencia temporal (Delta) ===
  const firstVal = isNum(series?.[0]) ? Number(series[0]) : null;
  const lastVal = isNum(series?.[series.length - 1]) ? Number(series[series.length - 1]) : null;

  let delta = null;
  let deltaText = null;
  let deltaColor = "var(--warn)";
  let DeltaIcon = null;

  if (isNum(firstVal) && isNum(lastVal)) {
    delta = lastVal - firstVal;
    deltaText = delta > 0 ? `+${fmtNum(delta)}` : fmtNum(delta);

    if (delta > 0) {
      deltaColor = "var(--ok)";
      // DeltaIcon = <ArrowUpwardRoundedIcon style={{ fontSize: ".6vw", color: deltaColor }} />;
      DeltaIcon = <TrendingUpIcon style={{ fontSize: "min(0.6vw, 1.2vh)", color: deltaColor }} />;
    } else if (delta < 0) {
      deltaColor = "var(--danger)";
      DeltaIcon = <TrendingDownIcon style={{ fontSize: "min(0.6vw, 1.2vh)", color: deltaColor }} />;
      // DeltaIcon = <ArrowDownwardRoundedIcon style={{ fontSize: ".6vw", color: deltaColor }} />;
    } else {
      deltaColor = "var(--warn)";
      // DeltaIcon = null;
      DeltaIcon = <TrendingFlatIcon style={{ fontSize: "min(0.6vw, 1.2vh)", color: deltaColor }} />;

    }
  }

  return (
    <div>
      <div className="metric__vals">
        <div className="metric__value">
          {value} {unit ? <span className="metric__unit"> {unit}</span> : null}
        </div>
        <div style={{display:'flex', justifyContent:'space-around', flexDirection:'column' }}>
          {/* Diferencia respecto al SetPoint */}
          {extraDiff && (
            <div
              className="metric__diff"
              style={{
                color: extraColor,
                display: "flex",
                alignItems: "center",
                alignContent:'center',
                gap: "0.3vw",
                marginRight: !showCharts && '0.4vw'
              }}
            >
              <span>{extraDiff}</span>
              {showCharts && <DiffIcon style={{ fontSize: "min(0.6vw, 1.2vh)", color: extraColor }} />}

            </div>
          )}

          {/* Tendencia temporal */}
          {deltaText && (
            <div
              className="metric__delta"
              style={{
                color: deltaColor,
                display: "flex",
                alignItems: "center",
                gap: "0.3vw",

              }}
            >
              <span>{deltaText}</span> 
              {showCharts && DeltaIcon}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Sparkline responsive con crosshair global ---------- */

function Sparkline({
  values = [],
  ts = [],
  min,
  max,
  height = "4vh",
  pad = 0,
  sharedIdx,
  strokeColor,
  Inyecting_O2 = [], // ahora se asegura de ser array
}) {
  const wrapRef = useRef(null);
  const { setHoverTS, setActive, setPosFromClient } = useContext(HoverCtx);
  const { width } = useResizeObserver(wrapRef);

  const pts = toArray(values);
  const inj = toArray(Inyecting_O2);
  // const w = Math.max(120, width || wrapRef.current?.clientWidth || 0);
  const w = Math.max(1, width || wrapRef.current?.clientWidth || 1);
  const h = typeof height === "string"
    ? parseFloat(window.innerHeight * (parseFloat(height) / 100))
    : height;

  // const stepX = useMemo(() => {
  //   if (!pts.length || w <= pad * 2) return 0;
  //   return (w - pad * 2) / Math.max(1, pts.length - 1);
  // }, [pts.length, w, pad]);

  const yOf = (v) => {
    const t = (v - min) / (max - min);
    return pad + (1 - t) * (h - pad * 2);
  };

  const validPts = pts
  .map((v, i) => ({ v: Number(v), i }))
  .filter(p => isNum(p.v));

  const stepX = validPts.length > 1
    ? (w - pad * 2) / (validPts.length - 1)
    : 0;

  return (
    <div className="sparklineWrap" ref={wrapRef}>
      <svg
        className="sparkline"
        width="100%"
        height="100%"
        viewBox={`0 0 ${w} ${h}`}
        // onMouseMove={onMove}
        // onMouseLeave={onLeave}
      >
        {/* Dibuja segmentos por tramo */}
        {/* {pts.length > 1 && (
          <>
            {pts.slice(1).map((curr, i) => {
              const prev = pts[i];
              if (!isNum(prev) || !isNum(curr)) return null;

              const x1 = pad + i * stepX;
              const x2 = pad + (i + 1) * stepX;
              const y1 = yOf(prev);
              const y2 = yOf(curr);

              // Determinar si estaba inyectando en este tramo
              const injecting = Boolean(inj[i]); // ← toma el valor del array
              const segmentColor = injecting ? "#ff5370ff" : "cyan";

              return (
                <line
                  key={i}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={segmentColor}
                  strokeWidth="0.5"
                  opacity="0.95"
                />
              );
            })}
          </>
        )} */}
        {validPts.slice(1).map((curr, idx) => {
          const prev = validPts[idx];

          const x1 = pad + idx * stepX;
          const x2 = pad + (idx + 1) * stepX;

          return (
            <line
              key={idx}
              x1={x1}
              y1={yOf(prev.v)}
              x2={x2}
              y2={yOf(curr.v)}
              stroke={inj[curr.i] ? "#ff5370ff" : "cyan"}
              strokeWidth="0.5"
              opacity="0.95"
            />
          );
        })}

        {/* Punto final */}
        {/* {isNum(pts[pts.length - 1]) && stepX > 0 ? (
          <circle
            cx={pad + (pts.length - 1) * stepX}
            cy={yOf(Number(pts[pts.length - 1]))}
            r="3"
            fill={strokeColor || "currentColor"}
          />
        ) : null} */}
        {validPts.length > 0 && stepX > 0 && (
          <circle
            cx={pad + (validPts.length - 1) * stepX}
            cy={yOf(validPts[validPts.length - 1].v)}
            r="3"
            fill={strokeColor || "currentColor"}
          />
        )}

        {/* Crosshair global */}
        {/* {sharedIdx != null && stepX > 0 && (
          <>
            <line
              x1={crossX}
              x2={crossX}
              y1={pad}
              y2={h - pad}
              stroke="currentColor"
              strokeWidth="1"
              opacity="0.15"
              strokeDasharray="4 4"
            />
            {isNum(pts[sharedIdx]) && (
              <circle cx={crossX} cy={focusY} r="3.2" fill={strokeColor || "currentColor"} />
            )}
          </>
        )} */}
      </svg>
    </div>
  );
}


/* ---------- Tooltip Global (sólo TS) ---------- */
function GlobalTooltip() {
  const { active, pos, hoverTS } = useContext(HoverCtx);
  if (!active || !hoverTS) return null;
  return (
    <div
      className="itgDash__globalTip"
      style={{ left: pos.x, top: pos.y, transform: "translate(5vw, -2vh)" }}
    >
      <div className="itgDash__globalTipRow">
        <span className="val">{formatAbs(hoverTS) ?? "—"}</span>
      </div>
    </div>
  );
}
/*---------- MetricSelector (botones globales) ------------*/ 
function MetricSelector() {
  const { activeMetric, setActiveMetric } = useContext(MetricCtx);
  const metrics = ["O2", "SO2", "Temp"];
  const activeIndex = metrics.indexOf(activeMetric);


  return (
    <div
      className="metricSelector metricSelector--slider"
      style={{ "--index": activeIndex }}
    >
      <span className="metricSelector__thumb" />
      {metrics.map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => setActiveMetric(m)}
          className={activeMetric === m ? "active" : ""}
        >
          {m}
        </button>
      ))}
    </div>
  );
}

function ChartsToggleButton({ translate }) {
  const { showCharts, toggleCharts } = useChartsVisibility();
  const Icon = showCharts ? VisibilityIcon : VisibilityOffIcon;

  return (
    <button
      type="button"
      className={`itgDash__chartsToggle ${showCharts ? "active" : ""}`}
      onClick={toggleCharts}
      aria-pressed={showCharts}
      title={showCharts ? t(translate, "dashboard_hide_charts", "Ocultar graficos") : t(translate, "dashboard_show_charts", "Mostrar graficos")}
    >
      <Icon className="itgDash__chartsToggleIcon" />
      <span>{showCharts ? t(translate, "dashboard_charts", "Graficos") : t(translate, "dashboard_no_charts", "Sin graficos")}</span>
    </button>
  );
}


/* ---------- Hooks / Utils ---------- */

function useResizeObserver(ref) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (cr) setSize({ width: cr.width, height: cr.height });
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

function toArray(x) { return Array.isArray(x) ? x : []; }
function lastOf(arr) { return arr && arr.length ? arr[arr.length - 1] : null; }
function lengthOrInf(arr) { return (arr && arr.length) ? arr.length : Infinity; }
function isNum(v) { return typeof v === "number" && Number.isFinite(v); }
function isNumArr(a) { return Array.isArray(a) && a.length > 0 && a.every((x) => isNum(Number(x))); }
function fmtNum(v) {
  if (!isNum(Number(v))) return "—";
  const n = Number(v);
  return new Intl.NumberFormat("es-CL", { maximumFractionDigits: 2 }).format(n);
}
function parseDashboardDateTime(value) {
  if (!value || typeof value !== "string") return null;
  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
function isMeasurementStale(value, minutes = 40) {
  const measuredAt = parseDashboardDateTime(value);
  if (!measuredAt) return true;
  const diffMs = Date.now() - measuredAt.getTime();
  return diffMs >= minutes * 60 * 1000;
}
function nullable(v) { return v == null ? "—" : String(v); }
function safeText(v) { return v ? String(v) : "—"; }
function boolish(v) { return v === true; }
function clamp(n, a, b) { return Math.max(a, Math.min(b, n)); }

function countTotals(modulos) {
  let modulo = modulos?.length || 0;
  let cans = 0;
  let bombas = 0;
  for (const m of modulos || []) {
    const c = m?.cans || [];
    cans += c.length;
    for (const can of c) bombas += (can?.bombas || []).length;
  }
  return { modulo, cans, bombas };
}

function formatAbs(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (isNaN(d)) return null;
  try {
    return new Intl.DateTimeFormat("es-CL", {
      timeZone: "America/Santiago",
      dateStyle: "medium",
      timeStyle: "short",
      hour12: false,
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
}
function formatRel(ts) {
  const d = new Date(ts);
  if (isNaN(d)) return "—";
  const now = new Date();
  const diff = Math.max(0, now - d);
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) return "justo ahora";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  return `hace ${days} d`;
}

function formatCenterStatsMeasurement(value) {
  const parsed = parseDashboardDateTime(value);
  if (!parsed) return "—";

  try {
    return new Intl.DateTimeFormat("es-CL", {
      timeZone: "America/Santiago",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(parsed);
  } catch {
    return value || "—";
  }
}

/** Primer número disponible en orden */
function firstAvailable(...vals) {
  for (const v of vals) if (isNum(Number(v))) return Number(v);
  return null;
}

/** Índice más cercano a un TS objetivo dentro de una lista de TS ISO */
function findNearestIdx(tsList, targetTS) {
  if (!Array.isArray(tsList) || !tsList.length || !targetTS) return null;
  const target = new Date(targetTS).getTime();
  if (!Number.isFinite(target)) return null;
  let bestI = 0;
  let bestD = Infinity;
  for (let i = 0; i < tsList.length; i++) {
    const t = new Date(tsList[i]).getTime();
    if (!Number.isFinite(t)) continue;
    const d = Math.abs(t - target);
    if (d < bestD) { bestD = d; bestI = i; }
  }
  return bestI;
}

/** Valor de una serie en el TS dado (nearest). Si falta, usa directo y luego bckp */
function valueAtOrBackup(bomba, seriesVals, seriesTS, targetTS, directKey, backupKey) {
  const sVals = toArray(seriesVals);
  const sTS = toArray(seriesTS);
  let val = null;

  if (targetTS && sVals.length && sTS.length) {
    const idx = findNearestIdx(sTS, targetTS);
    val = isNum(sVals[idx]) ? Number(sVals[idx]) : null;
  } else if (sVals.length) {
    val = isNum(lastOf(sVals)) ? Number(lastOf(sVals)) : null;
  }

  if (!isNum(val) && isNum(bomba?.[directKey])) val = Number(bomba[directKey]);
  if (!isNum(val) && isNum(bomba?.[backupKey])) val = Number(bomba[backupKey]);

  return val;
}

/** Promedios por módulo (O2, SO2, Temp) al TS (o últimos) */
function computeModuleAverages(modulos = [], targetTS = null) {
  const out = [];
  for (const m of modulos || []) {
    const label = (m?.cans?.[0]?.modulo != null) ? `Módulo ${m.cans[0].modulo}` : `Módulo ${m?.id ?? "?"}`;
    let sumO2 = 0, sumSO2 = 0, sumT = 0, cO2 = 0, cSO2 = 0, cT = 0;

    for (const can of (m?.cans || [])) {
      for (const b of (can?.bombas || [])) {
        const lm = b?.latest_measurements || {};
        const ts = lm?.TS;

        const vO2 = valueAtOrBackup(b, lm?.O2, ts, targetTS, "O2", "O2_bckp");
        if (isNum(vO2)) { sumO2 += vO2; cO2++; }

        const vSO2 = valueAtOrBackup(b, lm?.SO2, ts, targetTS, "SO2", "SO2_bckp");
        if (isNum(vSO2)) { sumSO2 += vSO2; cSO2++; }

        const vT = valueAtOrBackup(b, lm?.Temp, ts, targetTS, "Temp", "TEMP_bckp");
        if (isNum(vT)) { sumT += vT; cT++; }
      }
    }

    out.push({
      key: m?.id ?? label,
      label,
      o2: cO2 ? sumO2 / cO2 : null,
      so2: cSO2 ? sumSO2 / cSO2 : null,
      temp: cT ? sumT / cT : null,
    });
  }
  return out;
}
